import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { io } from 'socket.io-client'
import { markUnsent, withUnsent } from '../utils/unsent'
import * as api from '../services/api'
import { unreadCountFor, shouldAdoptServerReadPosition } from '../utils/unread'
import { AtriumOrder } from '@ofself/shell/atriumOrder'
import { ALL as ALL_WORLDS, planMove, applyOp, inverseOp, isNoop, worldName } from '../utils/worlds'
import {
  withFreshPhotos, nextPhotoRefreshMs, PHOTO_REFRESH_FALLBACK_MS,
} from '../utils/shellSpaces'

const CHAT_API = import.meta.env.VITE_CHAT_API_URL || ''
const ChatContext = createContext(null)

// ── Pre-auth fallback context ────────────────────────────────────────────────
// The ONE safe default for EVERY key the real provider exposes. The logged-out
// and still-loading branches spread this (plus their few live overrides) instead
// of hand-building parallel objects — those drifted from the real value three
// times, and each drift is a PROD-ONLY crash: a consumer that renders during the
// post-login loading window (e.g. ThreadView reading threadReplies.length in its
// effect deps) hits `undefined.length` and the whole tree dies under the splash.
// A dev-only parity check next to the real provider value warns the moment a new
// key is added there without a default here. Values are inert: no-op functions,
// empty collections, nulls — never-mutated module singletons are fine to share.
const noop = () => {}
const CHAT_CONTEXT_FALLBACK = {
  // identity / auth
  authenticated: false, currentUser: null, userAvatars: {}, uploadAvatar: noop, epStatus: null,
  // channels / groups / spaces
  activeChannel: null, selectChannel: noop, channels: [], groups: [], groupChannelsByGroup: {},
  activeGroup: null, selectGroup: noop, refreshGroupChannels: noop, createChannel: noop,
  createGroupChannel: noop, createGroup: noop, renameChannel: noop, deleteChannel: noop,
  setChannelProperties: noop,
  // worlds (a group's atrium)
  worldsByGroup: {}, activeWorld: '__all__', enterWorld: noop,
  createWorld: noop, renameWorld: noop, deleteWorld: noop, reorderWorlds: noop, moveChannels: noop, placeChannel: noop,
  leaveChannel: noop, addChannelMember: noop, removeChannelMember: noop, addMember: noop,
  // composing
  composingDM: false, startComposeDM: noop, cancelComposeDM: noop,
  composingChannel: null, startComposeChannel: noop, cancelComposeChannel: noop,
  // messages
  activeMessages: [], messages: [], sendMessage: noop, retryUnsent: noop, discardUnsent: noop, editMessage: noop, deleteMessage: noop,
  forwardMessage: noop, editingMessage: null, setEditingMessage: noop,
  replyingToMessage: null, setReplyingToMessage: noop,
  bulkEditMode: false, setBulkEditMode: noop, bulkSelectedMessages: new Set(), setBulkSelectedMessages: noop,
  hasMoreMessages: false, loadingOlder: false, loadOlderMessages: noop,
  jumpToMessageId: null, setJumpToMessageId: noop, navigateToMessage: noop,
  syncUrl: noop,
  addReaction: noop, removeReaction: noop,
  // DMs
  directMessages: [], dmChannels: [], fetchDMs: noop, createDM: noop, createPluginDM: noop,
  // members
  members: [], groupMembers: [], activeChannelMembers: [], showMembers: false, setShowMembers: noop,
  // thread
  showThread: false, threadMessage: null, threadReplies: [], openThread: noop, openThreadById: noop, closeThread: noop,
  sendThreadReply: noop,
  // unread / activity
  unreadCounts: {}, hasUnreadDMs: false, hasNewActivity: false, newActivityCount: 0, markActivitySeen: noop,
  markActivityTimestamp: null, lastMessageMap: {}, unreadDividerMsgId: null,
  emitTyping: noop, typingUsers: [], readReceipts: {}, markChannelRead: noop, markAsUnread: noop,
  mutedChannels: new Set(), toggleChannelMute: noop, pinnedChannels: new Set(), toggleChannelPin: noop,
  pinnedMessages: [], pinMessage: noop, unpinMessage: noop,
  // huddles / calls
  startHuddle: noop, joinHuddle: noop, leaveHuddle: noop, activeHuddle: null, joinedHuddle: null,
  activeHuddles: [], huddleParticipants: [], toggleHuddleMute: noop, toggleMute: noop, isMuted: false,
  toggleVideo: noop, isVideoOn: false, localStream: null, remoteStreams: {},
  toggleScreenShare: noop, isScreenSharing: false,
  recordingsHuddleId: null, openRecordingsSidebar: noop, closeRecordingsSidebar: noop,
  incomingCall: null, dismissIncomingCall: noop, joinHuddleFromNotification: noop,
}

// URL slug helpers
function slugify(name) {
  return (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'group'
}

// THE ADDRESS, in one place. The grammar:
//   /                      personal scope, no conversation — the home page
//   /:group                a space's HOME page (its front door)
//   /:group/:channel       a conversation
//   /dm/:username          a direct message, whatever space you are in
// Passing no channel means the scope's own root, which is the home page's
// address: the app's front door is the address you would type.
function pushUrl(group, channel, opts) {
  let path = '/'
  if (channel?.is_dm) {
    // DMs always get /dm/:username regardless of active group
    path = `/dm/${channel.other_username || channel.name || channel.id.slice(0, 8)}`
  } else if (group && channel) {
    path = `/${slugify(group.name)}/${channel.name || channel.id.slice(0, 8)}`
  } else if (group) {
    path = `/${slugify(group.name)}`
  }
  if (window.location.pathname !== path) {
    window.history[opts?.replace ? 'replaceState' : 'pushState'](null, '', path)
  }
}

// localStorage cache helpers
const CACHE_PREFIX = 'paradigm_cache_'
function cacheGet(key) {
  try { const v = localStorage.getItem(CACHE_PREFIX + key); return v ? JSON.parse(v) : null } catch { return null }
}
function cacheSet(key, val) {
  try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(val)) } catch {}
}

// Thread reply cache — persisted to localStorage, capped at 30 threads
function threadCacheWrite(cache) {
  const keys = Object.keys(cache)
  if (keys.length > 30) {
    keys.slice(0, keys.length - 30).forEach(k => delete cache[k])
  }
  cacheSet('thread_cache', cache)
}

export function ChatProvider({ children }) {
  const token = localStorage.getItem('chat_session_token')

  // Hydrate from cache instantly
  const [currentUser, setCurrentUser] = useState(() => cacheGet('currentUser'))
  const [channels, setChannels] = useState(() => cacheGet('channels') || [])
  const [groups, setGroups] = useState(() => cacheGet('groups') || [])
  // Exposure-profile status for the rail identity switcher: EP expiry + a
  // per-group authorization map ({ ep, groups_auth }).
  const [epStatus, setEpStatus] = useState(() => cacheGet('epStatus') || null)
  const [groupChannelsByGroup, setGroupChannelsByGroup] = useState(() => cacheGet('groupChannelsByGroup') || {})
  const [activeChannel, setActiveChannel] = useState(() => cacheGet('activeChannel'))
  const [activeGroup, setActiveGroup] = useState(() => cacheGet('activeGroup'))
  const [messages, setMessages] = useState(() => {
    const ch = cacheGet('activeChannel')
    return ch ? (cacheGet('messages_' + ch.id) || []) : []
  })
  const [composingDM, setComposingDM] = useState(false)
  const [composingChannel, setComposingChannel] = useState(null) // groupId or null
  // Never auto-open the thread panel on page load — it renders blank before replies
  // re-fetch. The threadMessage + reply cache are still restored for quick re-open.
  const [showThread, setShowThread] = useState(false)
  const [recordingsHuddleId, setRecordingsHuddleId] = useState(null)
  const [editingMessage, setEditingMessage] = useState(null)
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [bulkSelectedMessages, setBulkSelectedMessages] = useState(new Set())
  const [replyingToMessage, setReplyingToMessage] = useState(null)
  const [jumpToMessageId, setJumpToMessageId] = useState(null)
  const [threadMessage, setThreadMessage] = useState(() => cacheGet('threadMessage'))
  const [threadReplies, setThreadReplies] = useState(() => {
    const tm = cacheGet('threadMessage')
    if (!tm) return []
    const tcache = cacheGet('thread_cache') || {}
    return tcache[tm.id] || []
  })
  const [loading, setLoading] = useState(() => !!token && !cacheGet('currentUser'))
  /* THE ADDRESS THE APP OPENED WITH, read once during this provider's first
     render. The boot resolves it asynchronously — groups and channels have to
     arrive before a slug can be turned into a channel — and by then the page's
     own address sync has already corrected the bar to whatever it could
     resolve from cache. Reading location late is reading our own echo, and a
     deep link into a channel resolved to the cached one instead. */
  const bootPathRef = useRef(typeof window !== 'undefined' ? window.location.pathname : '/')
  const [dmChannels, setDmChannels] = useState(() => cacheGet('dmChannels') || [])
  const [members, setMembers] = useState([])
  // user_id -> avatar URL string (path). Populated from `user_avatar_updated`
  // socket events; overrides whatever URL the sender object has baked in,
  // so already-rendered avatars swap to the new image immediately.
  //
  // This map is persisted to localStorage, which was quietly unsafe while YOUR entry
  // held a Paradigm URL signed for an hour: a cold load the next morning restored a
  // dead signature, and it out-ranks the one the server just sent, so the photo stayed
  // broken until the refresh timer fired 45 minutes later. Every entry is now a
  // version-keyed proxy path with no signature in it, so storing it is safe.
  const [userAvatars, setUserAvatars] = useState(() => cacheGet('userAvatars') || {})
  const [groupMembers, setGroupMembers] = useState([])
  const [activeChannelMembers, setActiveChannelMembers] = useState(() => {
    const ch = cacheGet('activeChannel')
    return ch ? (cacheGet('channelMembers_' + ch.id) || []) : []
  })

  // A channel is held in four places (the channel list, the DM list, the
  // per-group map, and activeChannel) plus their caches. ONE helper patches
  // all of them, so a new channel-level field never has to remember the fan-out
  // again — every real-time channel update goes through here.
  const _patchChannelInState = useCallback((channelId, patch) => {
    const apply = ch => (ch.id === channelId ? { ...ch, ...patch } : ch)
    setChannels(prev => {
      const updated = prev.map(apply)
      cacheSet('channels', updated)
      return updated
    })
    setDmChannels(prev => {
      const updated = prev.map(apply)
      cacheSet('dmChannels', updated)
      return updated
    })
    setGroupChannelsByGroup(prev => {
      const updated = {}
      for (const [gid, chs] of Object.entries(prev)) updated[gid] = chs.map(apply)
      cacheSet('groupChannelsByGroup', updated)
      return updated
    })
    setActiveChannel(prev => {
      if (prev?.id !== channelId) return prev
      const updated = { ...prev, ...patch }
      cacheSet('activeChannel', updated)
      return updated
    })
  }, [])

  const [unreadCounts, setUnreadCounts] = useState({}) // channelId -> count
  const [hasMoreMessages, setHasMoreMessages] = useState(false) // whether older messages exist
  const [loadingOlder, setLoadingOlder] = useState(false) // loading older messages
  const [newActivityCount, setNewActivityCount] = useState(0)
  const hasNewActivity = newActivityCount > 0
  const [activeHuddle, setActiveHuddle] = useState(null)
  const [joinedHuddle, setJoinedHuddle] = useState(null) // persists across channel switches — the huddle the user is IN
  const [lastMessageMap, setLastMessageMap] = useState(() => cacheGet('lastMessageMap') || {})
  const [typingByChannel, setTypingByChannel] = useState({}) // channelId -> [{username, ts}]
  const [readReceipts, setReadReceipts] = useState({}) // userId -> { user_id, username, last_read_message_id, read_at }
  const [mutedChannels, setMutedChannels] = useState(() => new Set(cacheGet('mutedChannels') || []))
  const [pinnedChannels, setPinnedChannels] = useState(() => new Set(cacheGet('pinnedChannels') || []))
  const [pinnedMessages, setPinnedMessages] = useState(() => {
    const ch = cacheGet('activeChannel')
    return ch ? (cacheGet('pins_' + ch.id) || []) : []
  })
  const [unreadDividerMsgId, setUnreadDividerMsgId] = useState(null) // message id to show "New messages" divider after
  const [activeHuddles, setActiveHuddles] = useState({}) // channelId -> { huddle, channel_name, started_by }
  const [incomingCall, setIncomingCall] = useState(null) // { channelId, channelName, huddle, startedBy }

  // Refs for polling to avoid stale closures
  const activeChannelRef = useRef(null)
  const activeHuddleRef = useRef(null)
  const joinedHuddleRef = useRef(null)
  const ringingAudioRef = useRef(null)
  const ringingTimeoutRef = useRef(null)
  const mutedChannelsRef = useRef(new Set(cacheGet('mutedChannels') || []))
  const pinnedChannelsRef = useRef(new Set(cacheGet('pinnedChannels') || []))
  const messagesRef = useRef([])
  const currentUserRef = useRef(null)
  const lastReadRef = useRef({}) // channelId -> lastReadMessageId (persisted in localStorage)
  const threadPollSkipUntilRef = useRef(0) // skip thread poll until this timestamp
  const threadCacheRef = useRef(cacheGet('thread_cache') || {}) // messageId -> replies[] (persisted cache for instant thread open)
  const threadMessageRef = useRef(null) // always-current ref for socket handlers
  const typingThrottleRef = useRef(0) // timestamp of last typing emit
  const typingTimersRef = useRef({}) // username -> timeout id for auto-clear
  const recentlyViewedRef = useRef({}) // channelId -> timestamp of last view (suppress unread re-badge for 15s)
  // True once the user has explicitly picked a channel this session. The async
  // bootstrap restore must NOT override a selection the user made while the app
  // was still loading (that race sent messages to the wrong DM).
  const userSelectedChannelRef = useRef(false)

  // Keep refs in sync
  const activeHuddlesRef = useRef(activeHuddles)
  useEffect(() => { activeChannelRef.current = activeChannel }, [activeChannel])
  useEffect(() => { activeHuddleRef.current = activeHuddle }, [activeHuddle])
  useEffect(() => { joinedHuddleRef.current = joinedHuddle }, [joinedHuddle])
  useEffect(() => { activeHuddlesRef.current = activeHuddles }, [activeHuddles])
  useEffect(() => { messagesRef.current = messages }, [messages])
  useEffect(() => { currentUserRef.current = currentUser }, [currentUser])
  useEffect(() => { mutedChannelsRef.current = mutedChannels }, [mutedChannels])
  useEffect(() => { pinnedChannelsRef.current = pinnedChannels }, [pinnedChannels])
  useEffect(() => { threadMessageRef.current = threadMessage }, [threadMessage])

  // Load lastRead from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('chat_last_read')
      if (stored) lastReadRef.current = JSON.parse(stored)
    } catch {}
  }, [])

  // Bootstrap: fetch user, channels, DMs, first channel's messages
  useEffect(() => {
    if (!token) return

    async function init() {
      try {
        const [meRes, chRes, dmRes, groupsRes] = await Promise.all([
          api.getMe(),
          api.listChannels(),
          api.listDMs(),
          api.listGroups()
        ])
        const user = meRes.user

        const userData = {
          id: user.id,
          username: user.username || user.name,
          initials: (user.username || user.name || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
          status: 'online',
          color: 'var(--avatar-0)',
          avatar_url: user.avatar_url || null,
          avatar_version: user.avatar_version || null,
        }
        setCurrentUser(userData)
        cacheSet('currentUser', userData)

        setChannels(chRes.channels)
        cacheSet('channels', chRes.channels)
        setDmChannels(dmRes.channels)
        cacheSet('dmChannels', dmRes.channels)

        // Seed userAvatars from cold-load payloads so every UserAvatar
        // can show photos before any socket events fire — covers the
        // hero (current user) and sidebar DM partners.
        setUserAvatars(prev => {
          const next = { ...prev }
          if (user.id && user.avatar_url) next[user.id] = user.avatar_url
          for (const dm of (dmRes.channels || [])) {
            if (dm.other_user_id && dm.other_user_avatar_url) {
              next[dm.other_user_id] = dm.other_user_avatar_url
            }
          }
          cacheSet('userAvatars', next)
          return next
        })

        // Fetch muted channels
        api.getMutedChannels().then(res => {
          const ids = res.muted_channel_ids || []
          const s = new Set(ids)
          setMutedChannels(s)
          mutedChannelsRef.current = s
          cacheSet('mutedChannels', ids)
        }).catch(() => {})
        // Fetch pinned channels
        api.getPinnedChannels().then(res => {
          const ids = res.pinned_channel_ids || []
          const s = new Set(ids)
          setPinnedChannels(s)
          pinnedChannelsRef.current = s
          cacheSet('pinnedChannels', ids)
        }).catch(() => {})
        // Fetch exposure-profile status (EP expiry + per-group auth) for the rail
        api.getEpStatus().then(res => {
          setEpStatus(res)
          cacheSet('epStatus', res)
        }).catch(() => {})
        const groupList = groupsRes.groups || []
        setGroups(groupList)
        cacheSet('groups', groupList)

        const channelMap = {}
        groupList.forEach(group => { channelMap[group.id] = [] })
        ;(chRes.channels || []).forEach(ch => {
          if (!ch.group_id) return
          if (!channelMap[ch.group_id]) channelMap[ch.group_id] = []
          channelMap[ch.group_id].push(ch)
        })
        setGroupChannelsByGroup(channelMap)
        cacheSet('groupChannelsByGroup', channelMap)

        // Restore from the ARRIVAL URL first, then fall back to cached state
        const urlParts = bootPathRef.current.split('/').filter(Boolean)
        let initGroup = null
        let urlMatchedChannel = null

        if (urlParts.length >= 1 && urlParts[0] !== 'callback' && urlParts[0] !== 'dm') {
          // /:groupSlug or /:groupSlug/:channelName
          const groupSlug = urlParts[0]
          initGroup = groupList.find(g => slugify(g.name) === groupSlug) || null
          if (initGroup && urlParts[1]) {
            const channelName = urlParts[1]
            const groupChs = channelMap[initGroup.id] || []
            urlMatchedChannel = groupChs.find(c => c.name === channelName) || null
          }
        } else if (urlParts[0] === 'dm' && urlParts[1]) {
          // /dm/:username
          const dmTarget = urlParts[1]
          const allDms = dmRes.channels || []
          urlMatchedChannel = allDms.find(c => c.is_dm && (c.other_username === dmTarget || c.name === dmTarget)) || null
        }

        // Fall back to cached group if URL didn't match
        if (!initGroup && urlParts.length === 0) {
          const cachedGroup = cacheGet('activeGroup')
          initGroup = cachedGroup ? (groupList.find(g => g.id === cachedGroup.id) || null) : null
        }
        setActiveGroup(initGroup)
        cacheSet('activeGroup', initGroup)

        const allChannels = [...(chRes.channels || []), ...(dmRes.channels || [])]
        const savedId = sessionStorage.getItem('paradigm_active_channel')
        let restored = urlMatchedChannel || (savedId && allChannels.find(c => c.id === savedId))
        // Prevent desync: don't restore a group channel when we're in Home (no active group)
        if (restored && restored.group_id && !initGroup) {
          // Switch to that channel's group instead of ignoring it
          const restoredGroup = groupList.find(g => g.id === restored.group_id) || null
          if (restoredGroup) {
            initGroup = restoredGroup
            setActiveGroup(initGroup)
            cacheSet('activeGroup', initGroup)
          } else {
            restored = null // group doesn't exist anymore, pick a DM instead
          }
        }
        const dmList = dmRes.channels || []
        const startChannel = restored
          || (initGroup && channelMap[initGroup.id]?.[0])
          || (dmList.length > 0 ? dmList[0] : null)
          || (chRes.channels.length > 0 ? chRes.channels[0] : null)
        // If the user already clicked into a channel while this async load was
        // in flight, DO NOT override their choice with the restored/default
        // channel — doing so silently switched activeChannel (e.g. to the first
        // DM) after the user opened a different one, so the next message was
        // sent to the wrong conversation.
        if (startChannel && !userSelectedChannelRef.current) {
          const cachedCh = cacheGet('activeChannel')
          if (!cachedCh || cachedCh.id !== startChannel.id) {
            setActiveChannel(startChannel)
            cacheSet('activeChannel', startChannel)
          }
          /* NO ADDRESS WRITTEN HERE (2026-08-21). The boot RESTORES a channel
             — it does not navigate to one — and writing its path made a
             restored channel overrule whatever page the app actually opened
             on: landing on the home page at /engineering, the address turned
             into /engineering/backend for a conversation nobody had asked
             for. The address follows the PAGE now, written once by App's own
             sync (syncUrl below), so there is a single writer. */
        }
      } catch (err) {
        console.error('Failed to init chat:', err)
      } finally {
        setLoading(false)
      }
    }

    init()
  }, [token])

  // Each group's photo is a SIGNED Paradigm URL, so it expires and a tab left open
  // past the deadline would show a broken image. Paradigm now STATES that deadline
  // (`avatar_url_expires_at`), so we re-sign against the server's own number instead of
  // a constant copied out of another repository — see nextPhotoRefreshMs.
  //
  // YOUR photo is deliberately absent here. /api/chat/me returns this app's
  // version-keyed proxy URL now, which is addressed by content rather than carrying a
  // signature, so it never expires and there is nothing to re-mint. That is the whole
  // reason it goes through the proxy: no clock on the client.
  useEffect(() => {
    if (!token) return
    let timer = null
    let cancelled = false

    const tick = async () => {
      let fresh = null
      try {
        // Photos only: the channel state hanging off each group is left alone.
        const res = await api.listGroups()
        fresh = res?.groups || null
        setGroups(prev => withFreshPhotos(prev, fresh))
      } catch { /* keep what we have; the next tick asks again */ }
      if (cancelled) return
      timer = setTimeout(tick, nextPhotoRefreshMs(fresh))
    }

    timer = setTimeout(tick, PHOTO_REFRESH_FALLBACK_MS)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [token])

  // Fetch messages when channel changes (with cancellation for rapid switching)
  useEffect(() => {
    if (!token || !activeChannel) return

    const channelId = activeChannel.id

    // Track whether user has paginated in THIS session (not just cached from a previous one)
    const hasPaginatedKey = `paradigm_paginated_${channelId}`
    const hasPaginatedThisSession = sessionStorage.getItem(hasPaginatedKey) === 'true'

    // Only use cached messages if the user paginated THIS session (meaning the cache
    // has messages they loaded via scroll-up, not stale data from a previous session).
    // Previously this would always load stale localStorage cache, causing channels to
    // open at random positions in the middle instead of the bottom.
    if (hasPaginatedThisSession) {
      const cachedMsgs = cacheGet('messages_' + channelId)
      if (cachedMsgs && cachedMsgs.length > 0) {
        setMessages(cachedMsgs)
      }
    }

    let cancelled = false

    async function fetchMessages() {
      try {
        const res = await api.listMessages(channelId)

        // Always mark the active channel as read when we fetch its messages.
        // The user is viewing this channel — they'll see the messages.
        const fetchedMsgs = res.messages || []
        if (fetchedMsgs.length > 0) {
          const lastMsg = [...fetchedMsgs].reverse().find(m =>
            m.id && !m.id.startsWith?.('temp-')
          )
          if (lastMsg?.id) {
            lastReadRef.current[channelId] = lastMsg.id
            try {
              localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current))
            } catch {}
            if (localStorage.getItem('paradigm_read_receipts') !== 'off') {
              api.markChannelRead(channelId, lastMsg.id).catch(() => {})
            }
          }
        }

        // Only update UI state if user is still on this channel
        if (cancelled) return

        // Smart merge: keep older paginated messages that aren't in the fresh batch,
        // but ONLY if the user actually paginated in THIS session.
        // Stale cached messages from a previous session should be replaced, not kept.
        setMessages(prev => {
          // the server never saw an unsent message: it stays at the foot until retried or discarded
          const ofThis = (m) => m.channelId === channelId
          if (!prev || prev.length === 0 || !hasPaginatedThisSession) {
            // No existing messages OR messages are just from cache (not paginated this session)
            // → use fresh batch only
            const fresh = withUnsent(fetchedMsgs, prev, ofThis)
            cacheSet('messages_' + channelId, fresh)
            return fresh
          }
          const fetchedIds = new Set(fetchedMsgs.map(m => m.id))
          // Keep older messages that aren't in the fresh batch (they were paginated in)
          const olderKept = prev.filter(m => !fetchedIds.has(m.id) && !m._unsent)
          // Find the oldest fetched message's timestamp to only keep messages older than it
          const oldestFetchedTs = fetchedMsgs.length > 0
            ? new Date(fetchedMsgs[0].timestamp).getTime()
            : Infinity
          const olderOnly = olderKept.filter(m => {
            const ts = new Date(m.timestamp).getTime()
            return ts < oldestFetchedTs
          })
          const merged = withUnsent([...olderOnly, ...fetchedMsgs], prev, ofThis)
          cacheSet('messages_' + channelId, merged)
          return merged
        })
        // Only update hasMoreMessages if we DON'T have older paginated messages.
        // If we kept older messages, the previous pagination already set hasMoreMessages correctly.
        if (!hasPaginatedThisSession) {
          setHasMoreMessages(res.has_more || false)
        }

        // Clear unread divider and counts — user is viewing this channel, messages are loaded.
        // The unread divider was useful for the initial scroll-to-unread on channel open,
        // but once the fetch completes and messages are displayed, clear it.
        setUnreadDividerMsgId(null)
        setUnreadCounts(prev => {
          if (!prev[channelId]) return prev
          const next = { ...prev }
          delete next[channelId]
          return next
        })
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch messages:', err)
      }
    }

    fetchMessages()

    // Prefetch adjacent channels' messages in the background (for instant switching)
    const prefetchTimer = setTimeout(() => {
      if (cancelled) return
      const allChs = [...(channels || []), ...(dmChannels || [])]
      // Find siblings: same group channels + recent DMs
      const siblings = []
      if (activeChannel?.group_id) {
        const groupChs = (groupChannelsByGroup?.[activeChannel.group_id] || [])
          .filter(c => c.id !== channelId)
        siblings.push(...groupChs.slice(0, 3))
      }
      // Add top 3 DMs if we're in Home
      if (!activeChannel?.group_id) {
        siblings.push(...(dmChannels || []).filter(c => c.id !== channelId).slice(0, 3))
      }
      siblings.forEach(ch => {
        // Skip if already cached
        if (cacheGet('messages_' + ch.id)) return
        api.listMessages(ch.id, { limit: 30 }).then(res => {
          if (!cancelled && res.messages?.length) {
            cacheSet('messages_' + ch.id, res.messages)
          }
        }).catch(() => {})
      })
    }, 1500) // Wait 1.5s after current channel loads before prefetching

    return () => { cancelled = true; clearTimeout(prefetchTimer) }
  }, [token, activeChannel?.id])

  // Fetch members on init
  useEffect(() => {
    if (!token) return

    async function fetchMembers() {
      try {
        const res = await api.listMembers()
        setMembers(res.members || [])
      } catch (err) {
        console.error('Failed to fetch members:', err)
        setMembers([])
      }
    }

    fetchMembers()
  }, [token])

  // Pre-fetch group members when active channel is a group channel
  useEffect(() => {
    if (!token || !activeChannel?.group_id) {
      setGroupMembers([])
      return
    }
    let cancelled = false
    api.listGroupMembers(activeChannel.group_id).then(res => {
      if (!cancelled) setGroupMembers(res.members || [])
    }).catch(() => {
      if (!cancelled) setGroupMembers([])
    })
    return () => { cancelled = true }
  }, [token, activeChannel?.group_id])

  // Fetch active channel members (used for delivery/read status on messages)
  useEffect(() => {
    if (!token || !activeChannel?.id) {
      setActiveChannelMembers([])
      return
    }
    // Hydrate from cache instantly
    const cached = cacheGet('channelMembers_' + activeChannel.id)
    if (cached) setActiveChannelMembers(cached)
    let cancelled = false
    api.listChannelMembers(activeChannel.id).then(res => {
      if (!cancelled) {
        const fresh = res.members || []
        setActiveChannelMembers(fresh)
        cacheSet('channelMembers_' + activeChannel.id, fresh)
      }
    }).catch(() => {
      if (!cancelled && !cached) setActiveChannelMembers([])
    })
    return () => { cancelled = true }
  }, [token, activeChannel?.id])

  // Fetch pinned messages for active channel (with cache)
  useEffect(() => {
    if (!token || !activeChannel?.id) {
      setPinnedMessages([])
      return
    }
    // Hydrate from cache instantly
    const cached = cacheGet('pins_' + activeChannel.id)
    if (cached) setPinnedMessages(cached)
    let cancelled = false
    api.getPinnedMessages(activeChannel.id).then(res => {
      if (!cancelled) {
        const pins = res.pins || []
        setPinnedMessages(pins)
        cacheSet('pins_' + activeChannel.id, pins)
      }
    }).catch(() => {
      if (!cancelled) setPinnedMessages(cached || [])
    })
    return () => { cancelled = true }
  }, [token, activeChannel?.id])

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Shared AudioContext — created once on first user interaction, reused for all sounds.
  // Browsers block AudioContext before a gesture, so we lazily init + resume.
  const audioCtxRef = useRef(null)
  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)()
    }
    // Resume if suspended (browser autoplay policy)
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume()
    }
    return audioCtxRef.current
  }, [])

  // Unlock AudioContext on first click/keypress anywhere in the app
  useEffect(() => {
    const unlock = () => {
      getAudioCtx()
      document.removeEventListener('click', unlock)
      document.removeEventListener('keydown', unlock)
    }
    document.addEventListener('click', unlock, { once: true })
    document.addEventListener('keydown', unlock, { once: true })
    return () => {
      document.removeEventListener('click', unlock)
      document.removeEventListener('keydown', unlock)
    }
  }, [getAudioCtx])

  // Play a short ping sound for incoming messages
  const playNotificationSound = useCallback(() => {
    try {
      const ctx = getAudioCtx()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.setValueAtTime(880, ctx.currentTime) // A5 note
      osc.frequency.setValueAtTime(1174, ctx.currentTime + 0.1) // D6 note
      gain.gain.setValueAtTime(0.3, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.3)
    } catch (err) {
      console.warn('Notification sound failed:', err)
    }
  }, [getAudioCtx])

  // ─── Socket.IO real-time connection ────────────────────────────
  const socketRef = useRef(null)

  // Connect socket once we have a token
  useEffect(() => {
    if (!token) return

    const socket = io(CHAT_API || window.location.origin, {
      auth: { token },
      transports: ['websocket', 'polling'],
    })
    socketRef.current = socket

    socket.on('connect', () => {
    })

    // Logged out here or elsewhere (ecosystem logout, EP revoked). Drop the
    // session immediately. Also enforced lazily: the next API call 401s.
    socket.on('session_revoked', () => {
      console.log('[socket] session_revoked — logging out')
      api.logout()
    })

    // Real-time new messages — replaces message polling for the active channel
    socket.on('new_message', (msg) => {
      // Normalize: backend to_dict() sends 'channelId' (camelCase), but
      // frontend code expects 'channel_id' (snake_case) everywhere.
      if (!msg.channel_id && msg.channelId) msg.channel_id = msg.channelId
      const activeCh = activeChannelRef.current
      if (!activeCh || msg.channel_id !== activeCh.id) {
        // Update lastMessageMap even for non-active channels
        if (msg.channel_id && msg.content) {
          setLastMessageMap(prev => {
            const merged = {
              ...prev,
              [msg.channel_id]: {
                snippet: msg.content.slice(0, 100),
                timestamp: msg.timestamp || new Date().toISOString(),
                senderName: msg.sender?.username || '',
              }
            }
            cacheSet('lastMessageMap', merged)
            return merged
          })
        }
        // Play sound for non-active channel messages (unless muted or from self)
        if (msg.sender_id !== currentUserRef.current?.id &&
            !mutedChannelsRef.current.has(msg.channel_id) &&
            !notifiedMsgRef.current.has(msg.id)) {
          notifiedMsgRef.current.add(msg.id)
          persistNotified()
          playNotificationSound()
          if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            const sender = msg.sender?.username || 'Someone'
            new Notification(`${sender}`, {
              body: msg.content?.slice(0, 100) || 'New message',
              icon: '/favicon.ico',
              tag: msg.id
            })
          }
        }
        return
      }
      if (msg.sender_id === currentUserRef.current?.id) return

      // Update lastMessageMap for active channel
      setLastMessageMap(prev => {
        const merged = {
          ...prev,
          [msg.channel_id]: {
            snippet: msg.content?.slice(0, 100) || '',
            timestamp: msg.timestamp || new Date().toISOString(),
            senderName: msg.sender?.username || '',
          }
        }
        cacheSet('lastMessageMap', merged)
        return merged
      })

      // Thread replies belong in the thread panel, not main chat
      if (msg.thread_id || msg.parent_id) return

      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev
        // If we have a pending temp message from us, skip (sendMessage will resolve it)
        if (prev.some(m => m.id?.startsWith?.('temp-') && m.sender_id === msg.sender_id)) return prev
        // Only play sound + push if channel is not muted AND not already notified
        if (!mutedChannelsRef.current.has(msg.channel_id) && !notifiedMsgRef.current.has(msg.id)) {
          notifiedMsgRef.current.add(msg.id)
          persistNotified()
          playNotificationSound()
          if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            const sender = msg.sender?.username || 'Someone'
            const channelName = activeCh.is_dm ? sender : `#${activeCh.name}`
            new Notification(`${sender} in ${channelName}`, {
              body: msg.content?.slice(0, 100) || 'New message',
              icon: '/favicon.ico',
              tag: msg.id
            })
          }
        }
        const updated = [...prev, msg]
        cacheSet('messages_' + activeCh.id, updated)
        return updated
      })

      // Mark as read since user is viewing this channel
      if (msg.id && !msg.id.startsWith?.('temp-')) {
        lastReadRef.current[activeCh.id] = msg.id
        notifiedMsgRef.current.add(msg.id)
        persistNotified()
        try { localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current)) } catch {}
        if (localStorage.getItem('paradigm_read_receipts') !== 'off') {
          api.markChannelRead(activeCh.id, msg.id).catch(() => {})
        }
      }
    })

    // Real-time new DM
    socket.on('new_dm', (dm) => {
      setDmChannels(prev => {
        if (prev.some(d => d.id === dm.id)) return prev
        const updated = [...prev, dm]
        cacheSet('dmChannels', updated)
        return updated
      })
      socket.emit('join_channel', { channel_id: dm.id })
    })

    // Typing indicators
    socket.on('typing', (data) => {
      const { channel_id, username, user_id } = data
      if (!channel_id || !username) return
      setTypingByChannel(prev => {
        const existing = (prev[channel_id] || []).filter(t => t.username !== username)
        return { ...prev, [channel_id]: [...existing, { username, user_id, ts: Date.now() }] }
      })
      // Auto-clear after 3s
      const key = `${channel_id}:${username}`
      if (typingTimersRef.current[key]) clearTimeout(typingTimersRef.current[key])
      typingTimersRef.current[key] = setTimeout(() => {
        setTypingByChannel(prev => {
          const list = (prev[channel_id] || []).filter(t => t.username !== username)
          return { ...prev, [channel_id]: list }
        })
        delete typingTimersRef.current[key]
      }, 3000)
    })

    // Read receipts
    socket.on('message_read', (data) => {
      const { channel_id, user_id, username, last_read_message_id, read_at } = data
      const activeCh = activeChannelRef.current
      if (!activeCh || channel_id !== activeCh.id) return
      if (!last_read_message_id) return
      setReadReceipts(prev => {
        return {
          ...prev,
          [user_id]: { user_id, username, last_read_message_id, read_at: read_at || new Date().toISOString() }
        }
      })
    })

    // Reaction notifications — update message state + notify if it's your message
    socket.on('reaction_added', (data) => {
      const { message_id, channel_id, emoji, user_id: reactorId, username: reactorName } = data
      if (reactorId === currentUserRef.current?.id) return // own reaction, already applied optimistically

      // Update message reactions in state
      const activeCh = activeChannelRef.current
      if (activeCh && channel_id === activeCh.id) {
        setMessages(prev => prev.map(msg => {
          if (msg.id !== message_id) return msg
          const reactions = [...(msg.reactions || [])]
          const existing = reactions.find(r => r.emoji === emoji)
          if (existing) {
            if (!existing.user_ids?.includes(reactorId)) {
              existing.count = (existing.count || 0) + 1
              existing.user_ids = [...(existing.user_ids || []), reactorId]
            }
          } else {
            reactions.push({ emoji, count: 1, user_ids: [reactorId] })
          }
          return { ...msg, reactions }
        }))
      }

      // Notify if someone reacted to YOUR message
      const myMessages = messagesRef.current || []
      const reactedMsg = myMessages.find(m => m.id === message_id)
      if (reactedMsg && reactedMsg.sender_id === currentUserRef.current?.id) {
        setNewActivityCount(c => c + 1)
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`${reactorName || 'Someone'} reacted ${emoji}`, {
            body: reactedMsg.content?.slice(0, 80) || 'to your message',
            icon: '/favicon.ico',
            tag: `reaction-${message_id}-${emoji}`
          })
        }
      }
    })

    socket.on('reaction_removed', (data) => {
      const { message_id, channel_id, emoji, user_id: reactorId } = data
      if (reactorId === currentUserRef.current?.id) return

      const activeCh = activeChannelRef.current
      if (activeCh && channel_id === activeCh.id) {
        setMessages(prev => prev.map(msg => {
          if (msg.id !== message_id) return msg
          const reactions = (msg.reactions || []).map(r => {
            if (r.emoji !== emoji) return r
            const user_ids = (r.user_ids || []).filter(id => id !== reactorId)
            return { ...r, count: user_ids.length, user_ids }
          }).filter(r => r.count > 0)
          return { ...msg, reactions }
        }))
      }
    })

    // @mention notifications — ALWAYS fire, even on muted channels
    socket.on('mention', (data) => {
      if (data.sender_id === currentUserRef.current?.id) return
      const msgId = data.message?.id
      // Only fire notification if channel IS muted (unmuted channels handled by new_message)
      if (msgId && mutedChannelsRef.current.has(data.channel_id)) {
        playNotificationSound()
        if ('Notification' in window && Notification.permission === 'granted') {
          const sender = data.message?.sender?.username || 'Someone'
          const body = data.message?.content?.slice(0, 100) || 'mentioned you'
          new Notification(`${sender} mentioned you`, {
            body,
            icon: '/favicon.ico',
            tag: msgId
          })
        }
      }
    })

    // Thread reply notifications — ALWAYS fire, even on muted channels
    socket.on('thread_reply', (data) => {
      if (data.message?.sender_id === currentUserRef.current?.id) return
      playNotificationSound()
      if ('Notification' in window && Notification.permission === 'granted') {
        const sender = data.message?.sender?.username || 'Someone'
        new Notification(`${sender} replied in a thread`, {
          body: data.message?.content?.slice(0, 100) || 'New thread reply',
          icon: '/favicon.ico',
          tag: data.message?.id || 'thread-reply'
        })
      }
    })

    // Agent replies posted into a thread (emitted to channel room by personas_chat.py)
    socket.on('new_thread_reply', (msg) => {
      if (!msg?.id) return
      const openThread = threadMessageRef.current
      if (!openThread) return
      const threadRoot = msg.thread_id || msg.parent_id
      if (threadRoot !== openThread.id) return
      setThreadReplies(prev => {
        if (prev.some(r => r.id === msg.id)) return prev
        const updated = [...prev, msg]
        threadCacheRef.current[openThread.id] = updated
        threadCacheWrite(threadCacheRef.current)
        return updated
      })
      // Update reply count on the root message in the main list
      setMessages(prev => prev.map(m =>
        m.id === openThread.id ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m
      ))
      playNotificationSound()
    })

    // Real-time new channel
    socket.on('new_channel', (ch) => {
      setChannels(prev => {
        if (prev.some(c => c.id === ch.id)) return prev
        const updated = [...prev, ch]
        cacheSet('channels', updated)
        return updated
      })
      if (ch.group_id) {
        setGroupChannelsByGroup(prev => {
          const list = prev[ch.group_id] || []
          if (list.some(c => c.id === ch.id)) return prev
          return { ...prev, [ch.group_id]: [...list, ch] }
        })
      }
      socket.emit('join_channel', { channel_id: ch.id })
    })

    // ─── Huddle lifecycle socket events ───────────────────────
    socket.on('huddle_started', (data) => {
      const { channel_id, channel_name, huddle, started_by } = data
      // Update activeHuddles map (for sidebar indicator)
      setActiveHuddles(prev => ({ ...prev, [channel_id]: data }))

      // Check if user started this huddle or is already in a call BEFORE any state updates
      const curUser = currentUserRef.current
      const alreadyInCall = !!joinedHuddleRef.current
      const isSelf = huddle.created_by === curUser?.id

      // Update activeHuddle if it's the current channel
      const activeCh = activeChannelRef.current
      if (activeCh && activeCh.id === channel_id) {
        setActiveHuddle(huddle)
      }

      // Don't ring if user started the huddle themselves
      if (isSelf) return
      // Don't ring if user is already in a huddle
      if (alreadyInCall) return

      // Show incoming call panel
      setIncomingCall({
        channelId: channel_id,
        channelName: channel_name,
        huddle,
        startedBy: started_by,
      })
      // Start ringing synchronously (no setTimeout to avoid race with stopRinging)
      if (!mutedChannelsRef.current.has(channel_id)) {
        // Stop any existing ringing first
        if (ringingAudioRef.current) {
          ringingAudioRef.current.pause()
          ringingAudioRef.current = null
        }
        if (ringingTimeoutRef.current) {
          clearTimeout(ringingTimeoutRef.current)
          ringingTimeoutRef.current = null
        }
        try {
          const audio = new Audio('/sounds/huddle-ring.wav')
          audio.loop = true
          audio.volume = 0.6
          audio.play().catch(() => {})
          ringingAudioRef.current = audio
          ringingTimeoutRef.current = setTimeout(() => {
            if (ringingAudioRef.current) {
              ringingAudioRef.current.pause()
              ringingAudioRef.current.currentTime = 0
              ringingAudioRef.current = null
            }
            ringingTimeoutRef.current = null
          }, 15000)
        } catch {}
      }
      // Browser push notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`Call in ${channel_name || 'a channel'}`, {
          body: `${started_by || 'Someone'} started a call`,
          icon: '/favicon.ico',
          tag: 'huddle-' + huddle.id,
        })
      }
    })

    socket.on('huddle_updated', (data) => {
      const { channel_id, huddle } = data
      setActiveHuddles(prev => ({ ...prev, [channel_id]: data }))
      // Update activeHuddle if it's the current channel
      const activeCh = activeChannelRef.current
      if (activeCh && activeCh.id === channel_id) {
        setActiveHuddle(huddle)
      }
      // Update joinedHuddle if this is the huddle we're in (works across channel switches)
      if (huddleChannelIdRef.current === channel_id) {
        setJoinedHuddle(prev => {
          if (!prev) return prev
          if (prev.id !== huddle?.id) return prev
          if (JSON.stringify(prev.participants) !== JSON.stringify(huddle?.participants)) return huddle
          return prev
        })
      }
      // Update incoming call panel if it matches
      setIncomingCall(prev => {
        if (prev && prev.channelId === channel_id) {
          return { ...prev, huddle }
        }
        return prev
      })
    })

    socket.on('huddle_ended', (data) => {
      const { channel_id } = data
      setActiveHuddles(prev => {
        const updated = { ...prev }
        delete updated[channel_id]
        return updated
      })
      // Clear activeHuddle if it's the current channel
      const activeCh = activeChannelRef.current
      if (activeCh && activeCh.id === channel_id) {
        setActiveHuddle(null)
      }
      // Clear joinedHuddle if this was the huddle we were in
      if (huddleChannelIdRef.current === channel_id) {
        setJoinedHuddle(null)
        huddleChannelIdRef.current = null
      }
      // Clear incoming call + stop ringing if it matches
      setIncomingCall(prev => {
        if (prev && prev.channelId === channel_id) {
          // Stop ringing
          if (ringingAudioRef.current) {
            ringingAudioRef.current.pause()
            ringingAudioRef.current.currentTime = 0
            ringingAudioRef.current = null
          }
          if (ringingTimeoutRef.current) {
            clearTimeout(ringingTimeoutRef.current)
            ringingTimeoutRef.current = null
          }
          return null
        }
        return prev
      })
    })

    // Real-time message edit
    socket.on('message_edited', (data) => {
      // Update sidebar preview if the edited message is the latest
      if (data.channel_id && data.content) {
        setLastMessageMap(prev => {
          const entry = prev[data.channel_id]
          if (entry) {
            const updated = { ...prev, [data.channel_id]: { ...entry, snippet: data.content.slice(0, 100) } }
            cacheSet('lastMessageMap', updated)
            return updated
          }
          return prev
        })
      }

      const activeCh = activeChannelRef.current
      if (!activeCh || data.channel_id !== activeCh.id) return
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, content: data.content, edited: true, ...(data.video_call ? { video_call: data.video_call } : {}) } : m
      ))
      setThreadReplies(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, content: data.content, edited: true, ...(data.video_call ? { video_call: data.video_call } : {}) } : m
      ))
    })

    // Real-time message delete
    socket.on('message_deleted', (data) => {
      const activeCh = activeChannelRef.current
      if (activeCh && data.channel_id === activeCh.id) {
        setMessages(prev => prev.filter(m => m.id !== data.message_id))
        setThreadReplies(prev => prev.filter(m => m.id !== data.message_id))
      }
    })

    // Real-time channel deleted (by admin/creator)
    socket.on('channel_deleted', (data) => {
      const { channel_id, group_id } = data
      setChannels(prev => {
        const updated = prev.filter(c => c.id !== channel_id)
        cacheSet('channels', updated)
        return updated
      })
      setDmChannels(prev => {
        const updated = prev.filter(d => d.id !== channel_id)
        cacheSet('dmChannels', updated)
        return updated
      })
      if (group_id) {
        setGroupChannelsByGroup(prev => {
          const list = prev[group_id] || []
          const updated = { ...prev, [group_id]: list.filter(c => c.id !== channel_id) }
          cacheSet('groupChannelsByGroup', updated)
          return updated
        })
      }
      // If viewing the deleted channel, switch away
      const activeCh = activeChannelRef.current
      if (activeCh && activeCh.id === channel_id) {
        setActiveChannel(null)
      }
    })

    // Real-time avatar updates — fires when anyone (this user OR
    // someone else in their network) changes their profile photo.
    // We update the override map; every <UserAvatar /> instance
    // observing this context will pick up the new URL on the next
    // render without needing to refetch message lists.
    socket.on('user_avatar_updated', (data) => {
      if (!data?.user_id) return
      setUserAvatars(prev => {
        const next = { ...prev, [data.user_id]: data.avatar_url || null }
        cacheSet('userAvatars', next)
        return next
      })
      // Also patch currentUser if it's the local user — so the
      // settings hero refreshes even if the upload originated on
      // another device.
      setCurrentUser(prev => {
        if (!prev || prev.id !== data.user_id) return prev
        const updated = { ...prev, avatar_url: data.avatar_url, avatar_version: data.avatar_version }
        cacheSet('currentUser', updated)
        return updated
      })
    })

    // Real-time property ledger
    socket.on('channel_properties_updated', (data) => {
      _patchChannelInState(data.channel_id, {
        properties: data.properties,
        properties_hidden: data.properties_hidden,
      })
    })

    // Worlds: a group's rooms changed, or a channel moved between them
    socket.on('worlds_updated', (data) => { if (data?.group_id) loadWorlds(data.group_id) })
    socket.on('channel_worlds_updated', (data) => {
      _patchChannelInState(data.channel_id, { world_ids: data.world_ids || [] })
    })

    // Real-time channel renamed
    socket.on('channel_renamed', (data) => {
      _patchChannelInState(data.channel_id, { name: data.name })
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Join new channel rooms when active channel changes
  useEffect(() => {
    const socket = socketRef.current
    if (!socket || !activeChannel) return
    socket.emit('join_channel', { channel_id: activeChannel.id })
    // Also stay joined to the huddle's channel so we get huddle_updated events
    // even when browsing other channels
    const huddleChId = huddleChannelIdRef.current
    if (huddleChId && huddleChId !== activeChannel.id) {
      socket.emit('join_channel', { channel_id: huddleChId })
    }
  }, [activeChannel?.id])

  // Catch-up fetch on socket reconnect only (replaces the old 3s polling loop).
  // WebSocket is the primary real-time transport; we only fetch missed messages
  // when the socket reconnects after a disconnect.
  useEffect(() => {
    const socket = socketRef.current
    if (!socket || !token) return

    function handleReconnect() {
      const ch = activeChannelRef.current
      const msgs = messagesRef.current
      if (!ch || msgs.length === 0) return

      const lastRealMsg = [...msgs].reverse().find(m => !m.id?.startsWith?.('temp-'))
      const lastMsgId = lastRealMsg?.id
      if (!lastMsgId) return

      api.listMessages(ch.id, { after: lastMsgId }).then(res => {
        const newMsgs = res.messages || []
        if (newMsgs.length === 0) return

        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id))
          const pendingNow = prev.some(m => m.id?.startsWith?.('temp-'))
          const truly_new = newMsgs.filter(m => {
            if (existingIds.has(m.id)) return false
            if (pendingNow && m.sender_id === currentUserRef.current?.id) return false
            return true
          })
          if (truly_new.length === 0) return prev

          // Notify for messages missed during disconnect
          const unnotified = truly_new.filter(m =>
            m.sender_id !== currentUserRef.current?.id && !notifiedMsgRef.current.has(m.id)
          )
          if (unnotified.length > 0 && !mutedChannelsRef.current.has(ch.id)) {
            playNotificationSound()
            for (const m of unnotified) {
              notifiedMsgRef.current.add(m.id)
            }
            persistNotified()
          }

          const updated = [...prev, ...truly_new]
          cacheSet('messages_' + ch.id, updated)
          return updated
        })
      }).catch(() => {})
    }

    socket.on('reconnect', handleReconnect)
    return () => { socket.off('reconnect', handleReconnect) }
  }, [token, playNotificationSound])

  // Load older messages (scroll-up pagination)
  const loadOlderMessages = useCallback(async () => {
    const ch = activeChannelRef.current
    if (!ch || loadingOlder || !hasMoreMessages) return
    const msgs = messagesRef.current
    const oldestMsg = msgs.find(m => !m.id?.startsWith?.('temp-'))
    if (!oldestMsg) return

    setLoadingOlder(true)
    // Mark that this channel has been paginated in the current session
    // so the smart merge knows to keep older messages on re-fetch
    try { sessionStorage.setItem(`paradigm_paginated_${ch.id}`, 'true') } catch {}
    try {
      const res = await api.listMessages(ch.id, { before: oldestMsg.id, limit: 50 })
      const olderMsgs = res.messages || []
      if (olderMsgs.length === 0) {
        setHasMoreMessages(false)
        return
      }
      setHasMoreMessages(res.has_more || false)
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id))
        const unique = olderMsgs.filter(m => !existingIds.has(m.id))
        const merged = [...unique, ...prev]
        cacheSet('messages_' + ch.id, merged)
        return merged
      })
    } catch (err) {
      console.error('Failed to load older messages:', err)
    } finally {
      setLoadingOlder(false)
    }
  }, [loadingOlder, hasMoreMessages])

  const refreshGroupChannels = useCallback(async () => {
    try {
      const [groupsRes, chRes] = await Promise.all([
        api.listGroups(),
        api.listChannels()
      ])
      const groupList = groupsRes.groups || []
      const channelsList = chRes.channels || []
      setGroups(groupList)
      cacheSet('groups', groupList)
      setChannels(channelsList)
      cacheSet('channels', channelsList)

      const map = {}
      groupList.forEach(group => { map[group.id] = [] })
      channelsList.forEach(ch => {
        if (!ch.group_id) return
        if (!map[ch.group_id]) map[ch.group_id] = []
        map[ch.group_id].push(ch)
      })
      setGroupChannelsByGroup(map)
      cacheSet('groupChannelsByGroup', map)
    } catch (err) {
      console.error('Failed to refresh groups/channels:', err)
    }
  }, [])

  // Track which messages we've already notified about (persisted to survive refresh)
  const notifiedMsgRef = useRef(new Set(
    (() => { try { return JSON.parse(localStorage.getItem('chat_notified_msgs') || '[]') } catch { return [] } })()
  ))
  function persistNotified() {
    const arr = [...notifiedMsgRef.current].slice(-100)
    try { localStorage.setItem('chat_notified_msgs', JSON.stringify(arr)) } catch {}
  }

  // Poll unread info every 8 seconds
  useEffect(() => {
    if (!token) return

    async function pollUnread() {
      try {
        const res = await api.getUnread()
        const channelData = res.channels || {}
        const lastRead = lastReadRef.current
        const activeCh = activeChannelRef.current

        // Detect unknown channels
        const knownIds = new Set([
          ...(channels || []).map(c => c.id),
          ...(dmChannels || []).map(d => d.id)
        ])
        const hasUnknownChannel = Object.keys(channelData).some(id => !knownIds.has(id))
        if (hasUnknownChannel) {
          const [groupRes, chRes, dmRes] = await Promise.all([
            api.listGroups(),
            api.listChannels(),
            api.listDMs()
          ])
          const channelMap = {}
          ;(groupRes.groups || []).forEach(group => { channelMap[group.id] = [] })
          ;(chRes.channels || []).forEach(ch => {
            if (!ch.group_id) return
            if (!channelMap[ch.group_id]) channelMap[ch.group_id] = []
            channelMap[ch.group_id].push(ch)
          })
          setGroups(groupRes.groups || [])
          cacheSet('groups', groupRes.groups || [])
          setGroupChannelsByGroup(channelMap)
          cacheSet('groupChannelsByGroup', channelMap)
          setChannels(chRes.channels)
          cacheSet('channels', chRes.channels)
          setDmChannels(dmRes.channels)
          cacheSet('dmChannels', dmRes.channels)
        }

        const counts = {}
        const now = Date.now()
        for (const [channelId, info] of Object.entries(channelData)) {
          if (activeCh && channelId === activeCh.id && !manualUnreadRef.current.has(channelId)) {
            continue
          }
          // Suppress re-badging for channels viewed within the last 30 seconds
          // AND sync lastReadRef to the server's actual latest message
          // A channel explicitly marked unread must NOT be suppressed by the
          // recently-viewed window — otherwise the poll skips it and the
          // fallback below collapses its real count to a bare dot.
          const viewedAt = recentlyViewedRef.current[channelId]
          if (viewedAt && (now - viewedAt) < 30000 && !manualUnreadRef.current.has(channelId)) {
            // Sync lastReadRef to the server's true latest message so that once
            // the suppression window expires, the IDs match and no badge appears.
            // This handles the case where listMessages only returned 50 msgs
            // but the channel has more — the server knows the real last_message_id.
            if (info.last_message_id && lastRead[channelId] !== info.last_message_id) {
              lastReadRef.current[channelId] = info.last_message_id
              try { localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current)) } catch {}
              if (localStorage.getItem('paradigm_read_receipts') !== 'off') {
                api.markChannelRead(channelId, info.last_message_id).catch(() => {})
              }
            }
            continue
          }
          // Clean up old entries
          if (viewedAt && (now - viewedAt) >= 30000) delete recentlyViewedRef.current[channelId]
          // Skip channels where last message was sent by us (unless manually marked unread)
          if (info.last_sender_id === currentUserRef.current?.id && !manualUnreadRef.current.has(channelId)) continue

          const lastReadId = lastRead[channelId]
          // The server's unread_count is authoritative — it is computed from the
          // message_reads row, which the iOS app writes to as well, so it is the
          // only value that reflects reads from other devices. Local lastReadId
          // only breaks ties when the server omits the field entirely.
          const count = unreadCountFor(info, lastReadId)
          if (count > 0) counts[channelId] = count

          // Read on another device: the server says nothing is unread but our
          // localStorage id is stale. Adopt the server's position (and tell the
          // server we agree) so the two stop disagreeing on every poll.
          if (shouldAdoptServerReadPosition(info, lastReadId)) {
            lastReadRef.current[channelId] = info.last_message_id
            try { localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current)) } catch {}
          }

          // Sound / desktop notification stays gated on a local read position we
          // were already tracking: on a fresh browser lastReadId is absent and we
          // badge the backlog silently rather than replaying it as new alerts.
          if (count > 0 && lastReadId && lastReadId !== info.last_message_id) {
            if (info.last_sender_id !== currentUser?.id && !notifiedMsgRef.current.has(info.last_message_id)) {
              notifiedMsgRef.current.add(info.last_message_id)
              if (notifiedMsgRef.current.size > 200) {
                const arr = [...notifiedMsgRef.current]
                notifiedMsgRef.current = new Set(arr.slice(-100))
              }
              persistNotified()

              // Only play sound + push if channel is not muted
              if (!mutedChannelsRef.current.has(channelId)) {
                playNotificationSound()

                if ('Notification' in window && Notification.permission === 'granted') {
                  const allCh = [...(channels || []), ...(dmChannels || [])]
                  const ch = allCh.find(c => c.id === channelId)
                  const channelName = ch?.is_dm ? (ch.other_username || 'Direct Message') : (ch ? `#${ch.name}` : 'a channel')
                  new Notification(`New message in ${channelName}`, {
                    body: info.snippet || 'New message',
                    icon: '/favicon.ico',
                    tag: info.last_message_id
                  })
                }
              }
            }
          }
        }

        // Preserve manually-marked unreads the poll didn't pick up, keeping
        // their last known count so a real "2" never collapses to a bare dot.
        setUnreadCounts(prev => {
          for (const chId of manualUnreadRef.current) {
            if (!(chId in counts)) counts[chId] = prev[chId] || 1
          }
          return counts
        })

        // Build lastMessageMap from unread data
        const msgMap = {}
        for (const [channelId, info] of Object.entries(channelData)) {
          if (info.snippet || info.last_message_at) {
            let senderName = ''
            if (info.last_sender_id) {
              if (info.last_sender_id === currentUser?.id) {
                senderName = 'You'
              } else {
                const sender = (members || []).find(m => m.id === info.last_sender_id || m.user_id === info.last_sender_id)
                senderName = sender?.username || sender?.name || info.last_sender_name || ''
              }
            }
            msgMap[channelId] = {
              snippet: info.snippet || '',
              timestamp: info.last_message_at || '',
              senderName,
            }
          }
        }
        setLastMessageMap(() => {
          cacheSet('lastMessageMap', msgMap)
          return msgMap
        })
      } catch (err) {
        // Silently ignore
      }
    }

    pollUnread()
    const interval = setInterval(pollUnread, 8000)
    return () => clearInterval(interval)
  }, [token, currentUser?.id, playNotificationSound])

  // Poll thread replies every 4 seconds when thread is open
  useEffect(() => {
    if (!token || !showThread || !threadMessage) return

    const interval = setInterval(async () => {
      if (Date.now() < threadPollSkipUntilRef.current) return

      try {
        const res = await api.listThreadReplies(threadMessage.id)
        const newReplies = res.messages || []
        threadCacheRef.current[threadMessage.id] = newReplies; threadCacheWrite(threadCacheRef.current)
        setThreadReplies(prev => {
          // Compare against real (non-temp) messages to avoid temp/real duplicate
          const realPrev = prev.filter(m => !m.id?.startsWith?.('temp-') && !m._unsent)
          const hasTempIds = prev.some(m => m.id?.startsWith?.('temp-'))

          if (newReplies.length < realPrev.length) return prev
          if (newReplies.length !== realPrev.length || hasTempIds) {
            // Server has new replies OR we have temps to reconcile — server is source of truth
            const newOnes = newReplies.filter(r => !realPrev.some(p => p.id === r.id))
            const hasOtherReplies = newOnes.some(r => r.sender_id !== currentUser?.id)
            if (hasOtherReplies) {
              playNotificationSound()
              if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
                const lastReply = newOnes.find(r => r.sender_id !== currentUser?.id)
                if (lastReply) {
                  const sender = lastReply.sender?.username || 'Someone'
                  new Notification(`${sender} replied in thread`, {
                    body: lastReply.content?.slice(0, 100) || 'New reply',
                    icon: '/favicon.ico',
                    tag: lastReply.id
                  })
                }
              }
            }
            return withUnsent(newReplies, prev)
          }
          if (newReplies.length > 0 && realPrev.length > 0 &&
              newReplies[newReplies.length - 1].id !== realPrev[realPrev.length - 1].id) {
            return withUnsent(newReplies, prev)
          }
          return prev
        })
      } catch {}
    }, 4000)

    return () => clearInterval(interval)
  }, [token, showThread, threadMessage?.id, currentUser?.id, playNotificationSound])

  // Poll activity for new items every 10 seconds
  useEffect(() => {
    if (!token) return

    async function pollActivity() {
      try {
        const res = await api.getActivity()
        const activities = res.activities || []
        const lastSeenKey = 'chat_activity_last_seen'
        const lastSeen = localStorage.getItem(lastSeenKey) || '1970-01-01T00:00:00Z'

        const unseen = activities.filter(a => a.timestamp > lastSeen)
        // Server truth for the badge count; keep any socket-side bumps that
        // the (possibly lagging) feed hasn't caught up to yet.
        if (unseen.length > 0) {
          setNewActivityCount(c => Math.max(c, unseen.length))
        }
      } catch {}
    }

    pollActivity()
    const interval = setInterval(pollActivity, 10000)
    return () => clearInterval(interval)
  }, [token])

  // Huddle ringing — uses WAV file, loops for ~15 seconds
  const startRinging = useCallback(() => {
    stopRinging()
    try {
      const audio = new Audio('/sounds/huddle-ring.wav')
      audio.loop = true
      audio.volume = 0.6
      audio.play().catch(() => {})
      ringingAudioRef.current = audio
      // Auto-stop after 15 seconds
      ringingTimeoutRef.current = setTimeout(() => {
        stopRinging()
        setIncomingCall(prev => prev ? { ...prev, dismissed: true } : null)
      }, 15000)
    } catch {}
  }, [])

  const stopRinging = useCallback(() => {
    if (ringingAudioRef.current) {
      ringingAudioRef.current.pause()
      ringingAudioRef.current.currentTime = 0
      ringingAudioRef.current = null
    }
    if (ringingTimeoutRef.current) {
      clearTimeout(ringingTimeoutRef.current)
      ringingTimeoutRef.current = null
    }
  }, [])

  // Legacy single chime for non-ringing huddle notifications
  const playHuddleSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const times = [0, 0.15, 0.3]
      const freqs = [587, 784, 1047]
      times.forEach((t, i) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.setValueAtTime(freqs[i], ctx.currentTime + t)
        gain.gain.setValueAtTime(0.35, ctx.currentTime + t)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + t + 0.4)
        osc.start(ctx.currentTime + t)
        osc.stop(ctx.currentTime + t + 0.4)
      })
    } catch {}
  }, [])

  // Fetch active huddles on boot + periodically (for sidebar indicators)
  // Also auto-rejoin if the current user was in a huddle (e.g. page refresh)
  useEffect(() => {
    if (!token) return
    function fetchActiveHuddles() {
      api.getActiveHuddles().then(res => {
        const map = {}
        for (const h of (res.huddles || [])) {
          map[h.channel_id] = h
        }
        setActiveHuddles(map)

        // Auto-rejoin after page refresh: if sessionStorage has a huddle channel,
        // re-join the huddle (the beacon left it, so we need to POST again)
        const savedHuddleChannel = sessionStorage.getItem('paradigm_huddle_channel')
        if (savedHuddleChannel && !huddleChannelIdRef.current) {
          sessionStorage.removeItem('paradigm_huddle_channel')
          // Check if the huddle is still active before rejoining
          const activeHuddleForChannel = map[savedHuddleChannel]
          if (activeHuddleForChannel) {
            api.startHuddle(savedHuddleChannel).then(joinRes => {
              const huddle = joinRes.huddle
              setJoinedHuddle(huddle)
              setActiveHuddle(huddle)
              huddleChannelIdRef.current = savedHuddleChannel
            }).catch(err => {
              console.warn('[Huddle] Auto-rejoin failed:', err)
            })
          }
        } else if (!huddleChannelIdRef.current) {
          // Also check if user is somehow still a participant (e.g. beacon didn't fire)
          const curUser = currentUserRef.current
          if (curUser) {
            for (const h of (res.huddles || [])) {
              const huddle = h.huddle || h
              const participants = huddle.participants || []
              if (participants.some(p => p.user_id === curUser.id && !p.left_at)) {
                setJoinedHuddle(huddle)
                setActiveHuddle(huddle)
                huddleChannelIdRef.current = h.channel_id
                break
              }
            }
          }
        }
      }).catch(() => {})
    }
    fetchActiveHuddles()
    const interval = setInterval(fetchActiveHuddles, 60000)
    return () => clearInterval(interval)
  }, [token])

  // Poll huddle status for the HUDDLE's channel (not active channel) every 30s
  // This ensures the huddle state persists when browsing other channels
  const huddleChannelIdRef = useRef(null)

  // Track which channel the huddle is on — only managed by startHuddle/leaveHuddle now
  // (huddleChannelIdRef is set explicitly in startHuddle/joinHuddle/joinHuddleFromNotification
  //  and cleared in leaveHuddle — NOT driven by activeHuddle changes)

  // Poll the huddle's own channel, NOT the active/viewed channel
  // Uses joinedHuddle (persists across channel switches) instead of activeHuddle
  useEffect(() => {
    if (!token || !joinedHuddle) return
    const huddleChannelId = huddleChannelIdRef.current
    if (!huddleChannelId) return

    async function pollHuddle() {
      try {
        const res = await api.getHuddle(huddleChannelId)
        const next = res.huddle || null
        // Update joinedHuddle (persists across channel switches)
        setJoinedHuddle(prev => {
          if (!prev && !next) return prev
          if (prev?.id !== next?.id) return next
          if (JSON.stringify(prev?.participants) !== JSON.stringify(next?.participants)) return next
          return prev
        })
        // Also keep activeHuddle in sync if we're viewing the huddle's channel
        if (activeChannelRef.current?.id === huddleChannelId) {
          setActiveHuddle(prev => {
            if (!prev && !next) return prev
            if (prev?.id !== next?.id) return next
            if (JSON.stringify(prev?.participants) !== JSON.stringify(next?.participants)) return next
            return prev
          })
        }
        // Also update activeHuddles map
        if (next) {
          setActiveHuddles(prev => ({ ...prev, [huddleChannelId]: { channel_id: huddleChannelId, huddle: next } }))
        } else {
          setActiveHuddles(prev => {
            if (!prev[huddleChannelId]) return prev
            const updated = { ...prev }
            delete updated[huddleChannelId]
            return updated
          })
        }
      } catch {}
    }

    const interval = setInterval(pollHuddle, 30000)
    return () => clearInterval(interval)
  }, [token, joinedHuddle?.id])

  // Leave huddle on tab close/navigation via sendBeacon.
  // On refresh, we save the huddle channel to sessionStorage so we can auto-rejoin.
  // Uses huddleChannelIdRef (set on join, cleared on leave).
  useEffect(() => {
    function handleBeforeUnload() {
      const channelId = huddleChannelIdRef.current
      if (!channelId) return
      // Save for potential auto-rejoin on refresh (sessionStorage survives refresh, not tab close)
      sessionStorage.setItem('paradigm_huddle_channel', channelId)
      // Leave the huddle on the server
      const tkn = localStorage.getItem('chat_session_token')
      if (!tkn) return
      const url = `${CHAT_API}/api/chat/channels/${channelId}/huddle/leave`
      navigator.sendBeacon(url, JSON.stringify({ token: tkn }))
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handleBeforeUnload)
    }
  }, [])

  const startHuddle = useCallback(async () => {
    if (!activeChannel) return
    try {
      const res = await api.startHuddle(activeChannel.id)
      setActiveHuddle(res.huddle)
      setJoinedHuddle(res.huddle)
      huddleChannelIdRef.current = activeChannel.id
      // Dismiss incoming call popup if it was for this channel
      setIncomingCall(prev => prev?.channelId === activeChannel.id ? null : prev)
      stopRinging()
      return res.huddle
    } catch (err) {
      console.error('Failed to start huddle:', err)
    }
  }, [activeChannel?.id, stopRinging])

  const joinHuddle = useCallback(async () => {
    if (!activeChannel) return
    try {
      const res = await api.startHuddle(activeChannel.id)
      setActiveHuddle(res.huddle)
      setJoinedHuddle(res.huddle)
      huddleChannelIdRef.current = activeChannel.id
      setIncomingCall(prev => prev?.channelId === activeChannel.id ? null : prev)
      stopRinging()
      return res.huddle
    } catch (err) {
      console.error('Failed to join huddle:', err)
    }
  }, [activeChannel?.id, stopRinging])

  const leaveHuddle = useCallback(async () => {
    // Use the huddle's channel, not the currently viewed channel
    const channelId = huddleChannelIdRef.current || activeChannelRef.current?.id
    if (!channelId) return
    try {
      await api.leaveHuddle(channelId)
      huddleChannelIdRef.current = null
      setJoinedHuddle(null)
      // Re-fetch huddle state — it may still be active for other participants
      try {
        const res = await api.getHuddle(channelId)
        const huddle = res.huddle || null
        setActiveHuddle(huddle)
        if (huddle) {
          setActiveHuddles(prev => ({ ...prev, [channelId]: { channel_id: channelId, huddle } }))
        } else {
          setActiveHuddles(prev => { const u = { ...prev }; delete u[channelId]; return u })
        }
      } catch {
        setActiveHuddle(null)
      }
    } catch (err) {
      console.error('Failed to leave huddle:', err)
    }
  }, [])

  const toggleHuddleMute = useCallback(async () => {
    if (!activeHuddle) return
    try {
      const res = await api.toggleHuddleMute(activeHuddle.id)
      return res.is_muted
    } catch (err) {
      console.error('Failed to toggle mute:', err)
    }
  }, [activeHuddle?.id])

  // Clear activity badge AND persist timestamp so the poll doesn't re-flag
  const markActivitySeen = useCallback(() => {
    setNewActivityCount(0)
    localStorage.setItem('chat_activity_last_seen', new Date().toISOString())
  }, [])

  // Alias kept for backwards compat
  const markActivityTimestamp = markActivitySeen

  // Compute whether any DM has unread messages
  const hasUnreadDMs = dmChannels.some(dm => !!unreadCounts[dm.id])

  const createChannel = useCallback(async (name, type = 'public') => {
    try {
      const res = await api.createChannel({ name, type })
      const ch = res.channel
      setChannels(prev => {
        const updated = [...prev, ch]
        cacheSet('channels', updated)
        return updated
      })
      setActiveChannel(ch)
      cacheSet('activeChannel', ch)
      sessionStorage.setItem('paradigm_active_channel', ch.id)
      setMessages([])
      cacheSet('messages_' + ch.id, [])
      return ch
    } catch (err) {
      console.error('Failed to create channel:', err)
      throw err
    }
  }, [])

  const createGroupChannel = useCallback(async (groupId, name, description = '', memberIds = []) => {
    try {
      const res = await api.createGroupChannel(groupId, { name, description, member_ids: memberIds })
      const ch = res.channel
      setChannels(prev => {
        if (prev.some(p => p.id === ch.id)) return prev
        const updated = [...prev, ch]
        cacheSet('channels', updated)
        return updated
      })
      setGroupChannelsByGroup(prev => {
        const existing = prev[groupId] || []
        if (existing.some(c => c.id === ch.id)) return prev
        const updated = { ...prev, [groupId]: [...existing, ch] }
        cacheSet('groupChannelsByGroup', updated)
        return updated
      })
      setActiveChannel(ch)
      cacheSet('activeChannel', ch)
      sessionStorage.setItem('paradigm_active_channel', ch.id)
      setMessages([])
      cacheSet('messages_' + ch.id, [])
      return ch
    } catch (err) {
      console.error('Failed to create group channel:', err)
      throw err
    }
  }, [])

  // Typing indicator: throttled emit (max once per 2s)
  const emitTyping = useCallback((channelId) => {
    const now = Date.now()
    if (now - typingThrottleRef.current < 2000) return
    typingThrottleRef.current = now
    const socket = socketRef.current
    if (socket && currentUser) {
      socket.emit('typing', { channel_id: channelId, username: currentUser.username, user_id: currentUser.id })
    }
  }, [currentUser])

  // Dismiss incoming call panel (stop ringing, keep sidebar indicator)
  const dismissIncomingCall = useCallback(() => {
    setIncomingCall(null)
    stopRinging()
    // Belt-and-suspenders: forcibly kill any audio ref
    if (ringingAudioRef.current) {
      ringingAudioRef.current.pause()
      ringingAudioRef.current.currentTime = 0
      ringingAudioRef.current = null
    }
  }, [stopRinging])

  // Join huddle from incoming call notification
  const joinHuddleFromNotification = useCallback(async (channelId) => {
    stopRinging()
    setIncomingCall(null)
    // Belt-and-suspenders: forcibly kill any audio ref
    if (ringingAudioRef.current) {
      ringingAudioRef.current.pause()
      ringingAudioRef.current.currentTime = 0
      ringingAudioRef.current = null
    }
    try {
      const res = await api.startHuddle(channelId) // POST = join or start
      setActiveHuddle(res.huddle)
      setJoinedHuddle(res.huddle)
      huddleChannelIdRef.current = channelId
      // Switch to the channel
      const allCh = [...channels, ...dmChannels]
      const ch = allCh.find(c => c.id === channelId)
      if (ch) {
        setActiveChannel(ch)
        cacheSet('activeChannel', ch)
        sessionStorage.setItem('paradigm_active_channel', ch.id)
        setShowThread(false)
        setThreadMessage(null)
        setRecordingsHuddleId(null)
      }
    } catch (err) {
      console.error('Failed to join huddle from notification:', err)
    }
  }, [stopRinging, channels, dmChannels])

  // Toggle mute for a channel
  const toggleChannelMute = useCallback(async (channelId) => {
    try {
      const res = await api.toggleChannelMute(channelId)
      setMutedChannels(prev => {
        const next = new Set(prev)
        if (res.muted) {
          next.add(channelId)
        } else {
          next.delete(channelId)
        }
        mutedChannelsRef.current = next
        cacheSet('mutedChannels', [...next])
        return next
      })
    } catch (err) {
      console.error('Failed to toggle mute:', err)
    }
  }, [])

  // Toggle pin for a channel (pinned conversations)
  const toggleChannelPin = useCallback(async (channelId) => {
    try {
      const res = await api.toggleChannelPin(channelId)
      setPinnedChannels(prev => {
        const next = new Set(prev)
        if (res.pinned) {
          next.add(channelId)
        } else {
          next.delete(channelId)
        }
        pinnedChannelsRef.current = next
        cacheSet('pinnedChannels', [...next])
        return next
      })
    } catch (err) {
      console.error('Failed to toggle pin:', err)
    }
  }, [])

  // Pin a message in the active channel
  const pinMessage = useCallback(async (messageId) => {
    if (!activeChannel?.id) return
    const channelId = activeChannel.id
    try {
      await api.pinMessage(channelId, messageId)
      const res = await api.getPinnedMessages(channelId)
      const pins = res.pins || []
      setPinnedMessages(pins)
      cacheSet('pins_' + channelId, pins)
    } catch (err) {
      console.error('Failed to pin message:', err)
    }
  }, [activeChannel?.id])

  // Unpin a message in the active channel
  const unpinMessage = useCallback(async (messageId) => {
    if (!activeChannel?.id) return
    const channelId = activeChannel.id
    // Optimistic: remove from pinned and update cache
    setPinnedMessages(prev => {
      const updated = prev.filter(p => p.message_id !== messageId)
      cacheSet('pins_' + channelId, updated)
      return updated
    })
    try {
      await api.unpinMessage(channelId, messageId)
    } catch (err) {
      console.error('Failed to unpin message:', err)
      const res = await api.getPinnedMessages(channelId)
      const pins = res.pins || []
      setPinnedMessages(pins)
      cacheSet('pins_' + channelId, pins)
    }
  }, [activeChannel?.id])

  // Computed: typing users for active channel (excluding self).
  // Now returns objects `{ username, user_id }` so the typing pip
  // can render the user's avatar (live override via `userAvatars`).
  // String-shape consumers (older code or backend roundtrip while
  // user_id is missing) still work since UserAvatar treats userID
  // as optional and falls back to initials.
  const typingUsers = activeChannel
    ? (typingByChannel[activeChannel.id] || [])
        .filter(t => t.username !== currentUser?.username)
        .map(t => ({ username: t.username, user_id: t.user_id }))
    : []

  // Read receipts setting
  const readReceiptsEnabled = () => {
    return localStorage.getItem('paradigm_read_receipts') !== 'off'
  }

  const loadReadReceipts = useCallback(async (channelId) => {
    if (!channelId) {
      setReadReceipts({})
      return
    }
    try {
      const res = await api.getReadStatus(channelId)
      const readers = res.readers || []
      const receipts = {}
      for (const r of readers) {
        if (!r?.user_id || !r?.last_read_message_id) continue
        receipts[r.user_id] = r
      }
      setReadReceipts(receipts)
    } catch {
      setReadReceipts({})
    }
  }, [])

  // Mark channel as read
  const markChannelRead = useCallback(async (channelId, messageId) => {
    if (!readReceiptsEnabled()) return
    try {
      const res = await api.markChannelRead(channelId, messageId)
      return res
    } catch (err) {
      console.error('Failed to mark channel read:', err)
    }
  }, [])

  // Track channels manually marked as unread (so poll doesn't skip them)
  const manualUnreadRef = useRef(new Set())

  // Mark channel as unread from a specific message (or just mark as unread if no messageId)
  const markAsUnread = useCallback(async (channelId, messageId) => {
    if (!messageId) {
      // Simple mark-as-unread: just clear lastRead and set count to 1
      delete lastReadRef.current[channelId]
      try { localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current)) } catch {}
      manualUnreadRef.current.add(channelId)
      setUnreadCounts(prev => ({ ...prev, [channelId]: 1 }))
      return
    }
    try {
      const res = await api.markChannelUnread(channelId, messageId)
      // Update lastReadRef to the message before the target
      if (res.last_read_message_id) {
        lastReadRef.current[channelId] = res.last_read_message_id
      } else {
        delete lastReadRef.current[channelId]
      }
      try {
        localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current))
      } catch {}
      // Track as manually marked so poll doesn't skip it
      manualUnreadRef.current.add(channelId)
      // Set unread count from backend response
      setUnreadCounts(prev => ({ ...prev, [channelId]: res.unread_count || 1 }))
    } catch (err) {
      console.error('Failed to mark channel as unread:', err)
    }
  }, [])

  // The open channel's roster is re-read after any change to it, so every
  // surface that shows members (header stack, ledger, members tab) agrees.
  const refreshChannelMembers = useCallback(async (channelId) => {
    if (channelId !== activeChannel?.id) return
    const fresh = await api.listChannelMembers(channelId)
    const members = fresh.members || []
    setActiveChannelMembers(members)
    cacheSet('channelMembers_' + channelId, members)
  }, [activeChannel?.id])

  const addChannelMember = useCallback(async (channelId, userId, username) => {
    try {
      const res = await api.addChannelMember(channelId, userId, username)
      await refreshChannelMembers(channelId)
      return res.member
    } catch (err) {
      console.error('Failed to add channel member:', err)
      throw err
    }
  }, [refreshChannelMembers])

  const removeChannelMember = useCallback(async (channelId, userId) => {
    try {
      await api.removeChannelMember(channelId, userId)
      await refreshChannelMembers(channelId)
    } catch (err) {
      console.error('Failed to remove channel member:', err)
      throw err
    }
  }, [refreshChannelMembers])

  const sendMessage = useCallback(async (content, fileId, targetChannel) => {
    // Route to an explicit channel when given (e.g. the brand-new DM returned by
    // createDM during compose), NOT the possibly-stale activeChannel this callback
    // closed over. This is what stopped a composed message from landing in the
    // previously-open conversation while an empty DM was created for the intended
    // recipient.
    const channel = targetChannel || activeChannel
    if (!channel) return
    const chId = channel.id

    const replyTo = replyingToMessage
    const tempId = 'temp-' + Date.now()
    // When sending to the channel currently on screen, extend from its live
    // message list; otherwise build from that channel's own cache so we never
    // splice one conversation's messages into another's view or cache.
    const toActiveView = activeChannelRef.current?.id === chId
    const baseMsgs = toActiveView ? (messagesRef.current || []) : (cacheGet('messages_' + chId) || [])
    const lastMsg = baseMsgs[baseMsgs.length - 1]
    const optimisticTs = lastMsg?.timestamp
      ? new Date(new Date(lastMsg.timestamp).getTime() + 1).toISOString()
      : new Date().toISOString()
    const optimisticMsg = {
      id: tempId,
      channelId: chId,
      sender_id: currentUser?.id,
      sender_type: 'user',
      content,
      timestamp: optimisticTs,
      reactions: [],
      reply_count: 0,
      reply_to_id: replyTo?.id || null,
      reply_to: replyTo ? {
        id: replyTo.id,
        sender_id: replyTo.sender_id,
        sender_name: replyTo.sender?.username || 'Unknown',
        content: (replyTo.content || '').slice(0, 120),
      } : null,
      sender: {
        id: currentUser?.id,
        username: currentUser?.username || 'You',
        initials: currentUser?.initials || '??',
        color: currentUser?.color || '#6b7a52',
        status: 'online'
      }
    }
    if (replyTo) setReplyingToMessage(null)
    const withOptimistic = [...baseMsgs, optimisticMsg]
    cacheSet('messages_' + chId, withOptimistic)
    if (toActiveView) setMessages(withOptimistic)

    // Optimistically update lastMessageMap
    setLastMessageMap(prev => {
      const merged = {
        ...prev,
        [chId]: {
          snippet: content.slice(0, 100),
          timestamp: new Date().toISOString(),
          senderName: currentUser?.username || 'You',
        }
      }
      cacheSet('lastMessageMap', merged)
      return merged
    })

    const dedupeById = (list) => {
      const seen = new Set()
      return list.filter(m => {
        if (!m?.id) return true
        if (seen.has(m.id)) return false
        seen.add(m.id)
        return true
      })
    }
    try {
      const body = { content }
      if (fileId) body.file_id = fileId
      if (replyTo) body.reply_to_id = replyTo.id
      const res = await api.sendMessage(chId, body)
      const cached = cacheGet('messages_' + chId) || withOptimistic
      const deduped = dedupeById(
        cached.map(m => (m.id === tempId ? { ...res.message, sender: optimisticMsg.sender, _key: tempId } : m))
      )
      cacheSet('messages_' + chId, deduped)
      if (activeChannelRef.current?.id === chId) setMessages(deduped)
    } catch (err) {
      console.error('Failed to send message:', err)
      // Never drop the words: the row stays, marked unsent, with Retry and
      // Discard (UnsentNote). It leaves the `temp-` namespace so nothing
      // waits on it as an in-flight send.
      const unsent = markUnsent(optimisticMsg, fileId)
      const marked = (cacheGet('messages_' + chId) || withOptimistic).map(m => (m.id === tempId ? unsent : m))
      cacheSet('messages_' + chId, marked)
      if (activeChannelRef.current?.id === chId) setMessages(marked)
    }
  }, [activeChannel?.id, currentUser, replyingToMessage])

  const selectGroup = useCallback((group) => {
    setActiveGroup(group)
    cacheSet('activeGroup', group)
    setShowThread(false)
    setThreadMessage(null)
    setRecordingsHuddleId(null)
    if (!group) {
      // Home space — select first DM if available
      if (dmChannels?.length > 0) {
        const ch = dmChannels[0]
        setActiveChannel(ch)
        cacheSet('activeChannel', ch)
        sessionStorage.setItem('paradigm_active_channel', ch.id)
        setActiveHuddle(activeHuddlesRef.current[ch.id]?.huddle || null)
        pushUrl(null, ch)
      } else {
        setActiveChannel(null)
        setActiveHuddle(null)
        cacheSet('activeChannel', null)
        sessionStorage.removeItem('paradigm_active_channel')
        pushUrl(null, null)
      }
      return
    }
    // Auto-select first channel of that group
    const groupChs = groupChannelsByGroup[group.id] || []
    if (groupChs.length > 0) {
      const ch = groupChs[0]
      setActiveChannel(ch)
      cacheSet('activeChannel', ch)
      sessionStorage.setItem('paradigm_active_channel', ch.id)
      setActiveHuddle(activeHuddlesRef.current[ch.id]?.huddle || null)
      pushUrl(group, ch)
    } else {
      setActiveChannel(null)
      setActiveHuddle(null)
      cacheSet('activeChannel', null)
      sessionStorage.removeItem('paradigm_active_channel')
      pushUrl(group, null)
    }
  }, [groupChannelsByGroup, dmChannels])

  /* The address the current PAGE should be showing. App owns which page
     stands, this owns the grammar (pushUrl above), so neither has to know the
     other's business: App says "a conversation" or "the scope's root" and the
     path is written the one way. Replace, not push — a page change is a
     correction of the address, and synapse has no popstate handler to make
     the extra history entries mean anything. */
  const syncUrl = useCallback((channel) => {
    /* A CONVERSATION IS ADDRESSED BY ITS OWN SPACE, not by whichever one
       happens to be active: a group channel opened from search, a deep link,
       or a restore can belong to a space you are not standing in, and asking
       the active one for its name wrote the channel out of its group — every
       such path collapsed to "/". The channel names its group; the active
       space only answers for the scope root, which is what the home page is. */
    const group =
      (channel?.group_id && groups.find((g) => g.id === channel.group_id)) ||
      activeGroup ||
      cacheGet('activeGroup')
    pushUrl(group, channel || null, { replace: true })
  }, [groups, activeGroup])

  const startComposeDM = useCallback(() => {
    setComposingDM(true)
    setShowThread(false)
    setThreadMessage(null)
    setRecordingsHuddleId(null)
  }, [])

  const cancelComposeDM = useCallback(() => {
    setComposingDM(false)
  }, [])

  const startComposeChannel = useCallback((groupId) => {
    setComposingChannel(groupId)
    setComposingDM(false)
    setShowThread(false)
    setThreadMessage(null)
    setRecordingsHuddleId(null)
  }, [])

  const cancelComposeChannel = useCallback(() => {
    setComposingChannel(null)
  }, [])

  const selectChannel = useCallback((channel, { keepCompose } = {}) => {
    // Record that the user has made an explicit choice so the async bootstrap
    // restore won't clobber it and misroute the next message.
    userSelectedChannelRef.current = true
    if (!keepCompose) setComposingDM(false)
    setComposingChannel(null)
    // Mark the channel we're LEAVING as recently viewed (suppress unread re-badge)
    const prevCh = activeChannelRef.current
    const isSameChannel = prevCh && prevCh.id === channel.id
    if (prevCh && !isSameChannel) {
      recentlyViewedRef.current[prevCh.id] = Date.now()
    }
    setActiveChannel(channel)
    cacheSet('activeChannel', channel)
    sessionStorage.setItem('paradigm_active_channel', channel.id)
    // Update activeHuddle to match the new channel (or clear if no huddle there)
    const huddleData = activeHuddlesRef.current[channel.id]
    setActiveHuddle(huddleData?.huddle || huddleData || null)
    // Sync URL
    pushUrl(cacheGet('activeGroup'), channel)
    // Mark as recently viewed so the unread poll doesn't re-badge before the fetch completes
    recentlyViewedRef.current[channel.id] = Date.now()
    // Clear manual unread flag since user is actively reading this channel
    manualUnreadRef.current.delete(channel.id)
    // Only clear messages on ACTUAL channel switch — not when re-selecting the same channel.
    // Re-selecting the same channel won't re-trigger the fetch useEffect (deps: activeChannel.id),
    // so clearing messages here would leave a blank page with no way to reload.
    if (!isSameChannel) {
      // Render cached messages instantly when we need them for scroll restore — keeps the
      // DOM stable while fetch resolves, so restoreScrollPosition lands on the exact pixel
      // instead of fighting an empty-then-populated layout (the bug 397ec2c introduced).
      // Skip the cache when:
      //   - channel has unread → cache may be stale, fetch wins (avoids "see middle, then jump to unread")
      //   - no saved scroll for this channel → no restore target, just fetch + scrollToBottom is fine
      const cached = cacheGet('messages_' + channel.id)
      const channelHadUnread = !!unreadCounts[channel.id]
      const hasSavedScroll = (() => {
        try { return !!sessionStorage.getItem(`paradigm_scroll_${channel.id}`) } catch { return false }
      })()
      setMessages((!channelHadUnread && cached?.length && hasSavedScroll) ? cached : [])
    }
    setShowThread(false)
    setThreadMessage(null)
    setRecordingsHuddleId(null)
    if (channel.is_dm) {
      markActivitySeen()
    }
    // Immediately clear unread dot when user opens a channel
    setUnreadCounts(prev => {
      if (!prev[channel.id]) return prev
      const next = { ...prev }
      delete next[channel.id]
      return next
    })
    // Set unread divider ONLY if the channel actually has unread messages.
    // Previously this set the divider for every channel with a lastReadId,
    // causing false "unread" state even for fully-read channels.
    const lastReadId = lastReadRef.current[channel.id]
    const hasUnread = !!unreadCounts[channel.id]
    setUnreadDividerMsgId(hasUnread ? (lastReadId || null) : null)

    // Eagerly mark channel as read using cached messages so the unread poll
    // doesn't re-badge this channel before the network fetch completes
    const cachedMsgs = cacheGet('messages_' + channel.id) || []
    if (cachedMsgs.length > 0) {
      const lastCachedMsg = [...cachedMsgs].reverse().find(m => m.id && !m.id?.startsWith?.('temp-'))
      if (lastCachedMsg?.id) {
        lastReadRef.current[channel.id] = lastCachedMsg.id
        try { localStorage.setItem('chat_last_read', JSON.stringify(lastReadRef.current)) } catch {}
        if (localStorage.getItem('paradigm_read_receipts') !== 'off') {
          api.markChannelRead(channel.id, lastCachedMsg.id).catch(() => {})
        }
      }
    }
    // Fetch read receipts for this channel
    loadReadReceipts(channel.id)
    // THE CONVERSATION'S SPACE (owner call 2026-09-30: no conflation). A DM is
    // personal and a channel belongs to its group, so opening one from anywhere
    // (activity, search, a push) stands you in its space; the list only ever
    // shows the space you're in.
    if (channel.is_dm) {
      setActiveGroup(prev => {
        if (!prev) return prev
        cacheSet('activeGroup', null)
        return null
      })
    } else if (channel.group_id) {
      setActiveGroup(prev => {
        if (prev?.id === channel.group_id) return prev
        const g = groups.find(g => g.id === channel.group_id)
        if (g) {
          cacheSet('activeGroup', g)
          return g
        }
        return prev
      })
    }
  }, [markActivitySeen, groups, loadReadReceipts])

  // Navigate to a specific message — switch channel if needed, then scroll + highlight
  const navigateToMessage = useCallback((channelId, messageId) => {
    const allChannels = [...(channels || []), ...(dmChannels || [])]
    const ch = allChannels.find(c => c.id === channelId)
    if (ch && ch.id !== activeChannelRef.current?.id) {
      selectChannel(ch)
    }
    // Set jumpToMessageId so MessageList can scroll to it after render
    setJumpToMessageId(messageId)
  }, [channels, dmChannels, selectChannel])

  // Keep read receipts in sync for active channel even when selected implicitly (init/restore).
  useEffect(() => {
    loadReadReceipts(activeChannel?.id)
  }, [activeChannel?.id, loadReadReceipts])

  const openRecordingsSidebar = useCallback((huddleId) => {
    setRecordingsHuddleId(huddleId)
    // Close thread if open — only one side panel at a time
    setShowThread(false)
    cacheSet('threadMessage', null)
  }, [])

  const closeRecordingsSidebar = useCallback(() => {
    setRecordingsHuddleId(null)
  }, [])

  const closeThread = useCallback(() => {
    setShowThread(false)
    cacheSet('threadMessage', null)
    // Delay clearing data so exit animation can play with content still visible
    setTimeout(() => {
      setThreadMessage(null)
      setThreadReplies([])
    }, 300)
  }, [])

  const openThread = useCallback(async (message) => {
    // Toggle: if clicking the same thread that's already open, close it
    if (showThread && threadMessage?.id === message.id) {
      closeThread()
      return
    }
    setRecordingsHuddleId(null) // close recordings panel when thread opens
    setThreadMessage(message)
    setShowThread(true)
    cacheSet('threadMessage', message)
    markActivitySeen()
    // Instantly populate from cache (stale-while-revalidate)
    const cached = threadCacheRef.current[message.id]
    if (cached) setThreadReplies(cached)
    else setThreadReplies([])
    // Fetch fresh in background
    try {
      const res = await api.listThreadReplies(message.id)
      const fresh = res.messages || []
      threadCacheRef.current[message.id] = fresh; threadCacheWrite(threadCacheRef.current)
      setThreadReplies(fresh)
    } catch (err) {
      console.error('Failed to fetch thread replies:', err)
      if (!cached) setThreadReplies([])
    }
  }, [showThread, threadMessage?.id, closeThread, markActivitySeen])

  // OPEN A THREAD FROM AN ID ALONE — the one path for every surface that
  // holds a reply without holding its parent (activity rows, search hits,
  // notifications, a deep link). Those callers know a thread_id and nothing
  // else, and <openThread> needs the parent MESSAGE, so each of them was
  // going to fetch the channel, hunt the parent out of the list, and guess a
  // delay before opening. That is one procedure, so it lives once.
  //
  // Awaited, not timed: the thread opens when the parent is actually in hand.
  const openThreadById = useCallback(async (channelId, threadId, highlightMessageId) => {
    if (!threadId) return false
    navigateToMessage(channelId, highlightMessageId || threadId)
    try {
      const res = await api.listMessages(channelId, { limit: 50 })
      const parent = (res.messages || []).find(m => m.id === threadId)
      if (!parent) return false
      openThread(parent)
      return true
    } catch (err) {
      console.error('Failed to open thread:', err)
      return false
    }
  }, [navigateToMessage, openThread])


  const sendThreadReply = useCallback(async (content, fileId) => {
    if (!activeChannel || !threadMessage) return
    threadPollSkipUntilRef.current = Date.now() + 8000

    const tempId = 'temp-' + Date.now()
    const optimisticReply = {
      id: tempId,
      channelId: activeChannel.id,
      sender_id: currentUser?.id,
      sender_type: 'user',
      content: content,
      thread_id: threadMessage.id,
      timestamp: new Date().toISOString(), // will be corrected in setThreadReplies below
      reactions: [],
      reply_count: 0,
      sender: {
        id: currentUser?.id,
        username: currentUser?.username || 'You',
        initials: currentUser?.initials || '??',
        color: currentUser?.color || '#6b7a52',
        status: 'online'
      }
    }
    setThreadReplies(prev => {
      const lastReply = prev[prev.length - 1]
      if (lastReply?.timestamp) {
        optimisticReply.timestamp = new Date(new Date(lastReply.timestamp).getTime() + 1).toISOString()
      }
      const updated = [...prev, optimisticReply]
      threadCacheRef.current[threadMessage.id] = updated; threadCacheWrite(threadCacheRef.current)
      return updated
    })

    const parentId = threadMessage.id
    setMessages(prev => prev.map(m =>
      m.id === parentId ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m
    ))

    try {
      const res = await api.sendThreadReply(activeChannel.id, threadMessage.id, content, fileId)
      setThreadReplies(prev => {
        const replaced = prev.map(r => r.id === tempId ? { ...res.message, sender: r.sender, _key: tempId } : r)
        // Dedup in case the poll already added the real message
        const seen = new Set()
        return replaced.filter(r => {
          if (!r?.id) return true
          if (seen.has(r.id)) return false
          seen.add(r.id)
          return true
        })
      })

      // Thread replies to plugin messages are auto-invoked server-side by _maybe_auto_invoke_plugin
    } catch (err) {
      console.error('Failed to send thread reply:', err)
      // as in the conversation: the reply stays, unsent, with Retry and Discard
      setThreadReplies(prev => prev.map(r => (r.id === tempId ? markUnsent(r, fileId) : r)))
      setMessages(prev => prev.map(m =>
        m.id === parentId ? { ...m, reply_count: Math.max((m.reply_count || 1) - 1, 0) } : m
      ))
    }
  }, [activeChannel?.id, threadMessage?.id, currentUser])

  // An unsent message (the network or the server said no): take it out of the
  // stream, and on Retry send its words again to the conversation it was for.
  const discardUnsent = useCallback((msg) => {
    if (msg.thread_id) { setThreadReplies(prev => prev.filter(r => r.id !== msg.id)); return }
    const chId = msg.channelId
    const kept = (cacheGet('messages_' + chId) || []).filter(m => m.id !== msg.id)
    cacheSet('messages_' + chId, kept)
    if (activeChannelRef.current?.id === chId) {
      // the ref too, at once: a Retry sends next, and builds on the stream as the ref holds it
      messagesRef.current = (messagesRef.current || []).filter(m => m.id !== msg.id)
      setMessages(prev => prev.filter(m => m.id !== msg.id))
    }
  }, [])
  const retryUnsent = useCallback((msg) => {
    discardUnsent(msg)
    const fileId = msg._unsent?.fileId || undefined
    return msg.thread_id ? sendThreadReply(msg.content, fileId) : sendMessage(msg.content, fileId, { id: msg.channelId })
  }, [discardUnsent, sendMessage, sendThreadReply])

  // Reaction actions
  const addReaction = useCallback(async (messageId, emoji) => {
    if (!currentUser) return

    function applyAdd(msg) {
      if (msg.id !== messageId) return msg
      const reactions = [...(msg.reactions || [])]
      const existing = reactions.find(r => r.emoji === emoji)
      if (existing) {
        if (!existing.user_ids.includes(currentUser.id)) {
          existing.count += 1
          existing.user_ids = [...existing.user_ids, currentUser.id]
        }
      } else {
        reactions.push({ emoji, count: 1, user_ids: [currentUser.id] })
      }
      return { ...msg, reactions }
    }

    setMessages(prev => prev.map(applyAdd))
    setThreadReplies(prev => prev.map(applyAdd))

    try {
      await api.addReaction(messageId, emoji)
    } catch (err) {
      console.error('Failed to add reaction:', err)
    }
  }, [currentUser?.id])

  const removeReaction = useCallback(async (messageId, emoji) => {
    if (!currentUser) return

    function applyRemove(msg) {
      if (msg.id !== messageId) return msg
      let reactions = [...(msg.reactions || [])]
      const existing = reactions.find(r => r.emoji === emoji)
      if (existing) {
        existing.count -= 1
        existing.user_ids = existing.user_ids.filter(id => id !== currentUser.id)
        if (existing.count <= 0) {
          reactions = reactions.filter(r => r.emoji !== emoji)
        }
      }
      return { ...msg, reactions }
    }

    setMessages(prev => prev.map(applyRemove))
    setThreadReplies(prev => prev.map(applyRemove))

    try {
      await api.removeReaction(messageId, emoji)
    } catch (err) {
      console.error('Failed to remove reaction:', err)
    }
  }, [currentUser?.id])

  // Forward message to another channel
  const forwardMessage = useCallback(async (targetChannelId, originalMessage) => {
    const senderName = originalMessage.sender?.username || 'Unknown'
    const content = `> **Forwarded from ${senderName}:**\n> ${originalMessage.content.replace(/\n/g, '\n> ')}`
    const body = { content }
    if (originalMessage.file_id) body.file_id = originalMessage.file_id
    try {
      await api.sendMessage(targetChannelId, body)
    } catch (err) {
      console.error('Failed to forward message:', err)
    }
  }, [])

  // Edit a message (optimistic update + API call)
  const editMessage = useCallback(async (messageId, newContent) => {
    // Can't edit optimistic messages that haven't been saved to the server yet
    if (messageId?.startsWith?.('temp-')) {
      console.warn('Cannot edit unsaved message:', messageId)
      return
    }

    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, content: newContent, edited: true } : m
    ))
    setThreadReplies(prev => prev.map(m =>
      m.id === messageId ? { ...m, content: newContent, edited: true } : m
    ))

    try {
      await api.editMessage(messageId, newContent)
      // Update cache
      const ch = activeChannelRef.current
      if (ch) {
        setMessages(prev => {
          cacheSet('messages_' + ch.id, prev)
          return prev
        })
      }
    } catch (err) {
      console.error('Failed to edit message:', err)
    }
  }, [])

  // Delete a message (optimistic removal + API call)
  const deleteMessage = useCallback(async (messageId) => {
    setMessages(prev => {
      const filtered = prev.filter(m => m.id !== messageId)
      // Update sidebar snippet to the new last message
      const ch = activeChannelRef.current
      if (ch) {
        const lastMsg = [...filtered].reverse().find(m => !m.id?.startsWith?.('temp-'))
        setLastMessageMap(prevMap => {
          const updated = {
            ...prevMap,
            [ch.id]: lastMsg ? {
              snippet: lastMsg.content?.slice(0, 100) || '',
              timestamp: lastMsg.timestamp || '',
              senderName: lastMsg.sender?.username || '',
            } : { snippet: '', timestamp: '', senderName: '' }
          }
          cacheSet('lastMessageMap', updated)
          return updated
        })
        cacheSet('messages_' + ch.id, filtered)
      }
      return filtered
    })
    setThreadReplies(prev => prev.filter(m => m.id !== messageId))

    try {
      await api.deleteMessage(messageId)
    } catch (err) {
      console.error('Failed to delete message:', err)
    }
  }, [])

  // DM actions
  const fetchDMs = useCallback(async () => {
    try {
      const res = await api.listDMs()
      setDmChannels(res.channels)
      cacheSet('dmChannels', res.channels)
    } catch (err) {
      console.error('Failed to fetch DMs:', err)
    }
  }, [])

  const createDM = useCallback(async (userIds, usernames = {}) => {
    try {
      const res = await api.createDM(userIds, usernames)
      const ch = res.channel
      setDmChannels(prev => {
        if (prev.find(d => d.id === ch.id)) return prev
        const updated = [...prev, ch]
        cacheSet('dmChannels', updated)
        return updated
      })
      selectChannel(ch)
      return ch
    } catch (err) {
      console.error('Failed to create DM:', err)
      throw err
    }
  }, [selectChannel])

  const createPluginDM = useCallback(async (pluginId, pluginName, subEntityKey) => {
    try {
      const res = await api.createPluginDM(pluginId, pluginName, subEntityKey)
      const ch = res.channel
      setDmChannels(prev => {
        if (prev.find(d => d.id === ch.id)) return prev
        const updated = [...prev, ch]
        cacheSet('dmChannels', updated)
        return updated
      })
      selectChannel(ch)
      return ch
    } catch (err) {
      console.error('Failed to create plugin DM:', err)
      throw err
    }
  }, [selectChannel])


  // Helper to remove a channel from all state
  const _removeChannelFromState = useCallback((channelId) => {
    setChannels(prev => {
      const updated = prev.filter(c => c.id !== channelId)
      cacheSet('channels', updated)
      return updated
    })
    setDmChannels(prev => {
      const updated = prev.filter(d => d.id !== channelId)
      cacheSet('dmChannels', updated)
      return updated
    })
    setGroupChannelsByGroup(prev => {
      const updated = {}
      for (const [gid, chs] of Object.entries(prev)) {
        updated[gid] = chs.filter(c => c.id !== channelId)
      }
      cacheSet('groupChannelsByGroup', updated)
      return updated
    })
    // If this was the active channel, switch to first available
    setActiveChannel(prev => {
      if (prev?.id !== channelId) return prev
      // Pick next channel from groups or DMs
      const allCh = [...(channels || []), ...(dmChannels || [])].filter(c => c.id !== channelId)
      const next = allCh[0] || null
      if (next) {
        cacheSet('activeChannel', next)
        sessionStorage.setItem('paradigm_active_channel', next.id)
      }
      return next
    })
    // Clean up caches
    try {
      localStorage.removeItem('paradigm_cache_messages_' + channelId)
      localStorage.removeItem('paradigm_cache_channelMembers_' + channelId)
      localStorage.removeItem('paradigm_cache_pins_' + channelId)
    } catch {}
  }, [channels, dmChannels])

  // Leave a channel (member removal — still exists for others)
  const leaveChannel = useCallback(async (channelId) => {
    // Optimistic: remove from UI immediately
    _removeChannelFromState(channelId)
    try {
      await api.leaveChannel(channelId)
    } catch (err) {
      console.error('Failed to leave channel:', err)
      // Channel already removed from UI — don't rollback (user wanted to leave)
    }
  }, [_removeChannelFromState])

  // Rename a channel (creator only)
  const renameChannel = useCallback(async (channelId, newName) => {
    try {
      await api.renameChannel(channelId, newName)
      // Socket event handles state updates for all users (including self)
    } catch (err) {
      console.error('Failed to rename channel:', err)
    }
  }, [])

  // The channel's property ledger. Optimistic: the ledger is what you are
  // directly manipulating, so it has to answer under the cursor; the socket
  // echo then carries the same state to everyone else.
  const setChannelProperties = useCallback(async (channelId, properties, hidden) => {
    _patchChannelInState(channelId, { properties, properties_hidden: hidden })
    try {
      await api.setChannelProperties(channelId, properties, hidden)
    } catch (err) {
      console.error('Failed to save channel properties:', err)
    }
  }, [_patchChannelInState])

  // ── WORLDS (owner call 2026-10-01): a group's atrium rooms, shared by the
  // group (its `workspace` nodes); a channel lists the worlds it stands in.
  // The world you're in is yours, remembered per group (utils/worlds.js).
  const [worldsByGroup, setWorldsByGroup] = useState(() => cacheGet('worldsByGroup') || {})
  const [worldByGroup, setWorldByGroup] = useState(() => {
    try { return JSON.parse(localStorage.getItem('synapse_world_by_group') || '{}') } catch { return {} }
  })
  const activeWorld = (activeGroup && worldByGroup[activeGroup.id]) || ALL_WORLDS

  const loadWorlds = useCallback(async (groupId) => {
    if (!groupId) return
    try {
      const { worlds } = await api.listWorlds(groupId)
      setWorldsByGroup(prev => { const next = { ...prev, [groupId]: worlds || [] }; cacheSet('worldsByGroup', next); return next })
    } catch (err) {
      console.error('Failed to load worlds:', err)
    }
  }, [])
  useEffect(() => { if (activeGroup?.id) loadWorlds(activeGroup.id) }, [activeGroup?.id, loadWorlds])

  const enterWorld = useCallback((groupId, worldId) => {
    setWorldByGroup(prev => {
      const next = { ...prev, [groupId]: worldId }
      try { localStorage.setItem('synapse_world_by_group', JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

  const createWorld = useCallback(async (groupId, name) => {
    try {
      const { world } = await api.createWorld(groupId, name)
      setWorldsByGroup(prev => ({ ...prev, [groupId]: [...(prev[groupId] || []), world] }))
      return world
    } catch (err) {
      console.error('Failed to make world:', err)
      return null
    }
  }, [])

  const renameWorld = useCallback(async (groupId, worldId, name) => {
    setWorldsByGroup(prev => ({ ...prev, [groupId]: (prev[groupId] || []).map(w => (w.id === worldId ? { ...w, name } : w)) }))
    try { await api.updateWorld(groupId, worldId, { name }) } catch (err) { console.error('Failed to rename world:', err); loadWorlds(groupId) }
  }, [loadWorlds])

  // Its channels let go of it (the server does the same and tells everyone)
  const deleteWorld = useCallback(async (groupId, worldId) => {
    try {
      await api.deleteWorld(groupId, worldId)
      setWorldsByGroup(prev => ({ ...prev, [groupId]: (prev[groupId] || []).filter(w => w.id !== worldId) }))
      if (worldByGroup[groupId] === worldId) enterWorld(groupId, ALL_WORLDS)
    } catch (err) {
      console.error('Failed to remove world:', err)
    }
  }, [worldByGroup, enterWorld])

  /* THE GROUP'S ORDER (the atrium's drag): the worlds' ids in their new order,
     kept as ranks on their nodes (the shell's AtriumOrder: one write for one
     move), shown at once and put back if the server refuses. Resolves whether
     it was kept (false: the atrium puts its tiles back). */
  const reorderWorlds = useCallback(async (groupId, ids) => {
    const ranks = Object.fromEntries((worldsByGroup[groupId] || []).map(w => [w.id, w.order]))
    const show = (writes) => setWorldsByGroup(prev => {
      const by = new Map(writes.map(w => [w.id, w.order]))
      return { ...prev, [groupId]: (prev[groupId] || []).map(w => (by.has(w.id) ? { ...w, order: by.get(w.id) } : w)) }
    })
    const { ok, error } = await AtriumOrder.commit(ids, ranks, { show, write: (id, order) => api.updateWorld(groupId, id, { order }) })
    if (!ok) console.error('Failed to keep the worlds\' order:', error)
    return ok
  }, [worldsByGroup])

  /* A CHANNEL'S WORLDS CHANGE BY OPS (utils/worlds.js planMove): each shown at
     once, written as { add, remove } on what the channel holds at write time,
     and put back if the server refuses it. Resolves to what was done (with
     what each channel stood in before, for an undo) and what failed. */
  const runWorldOps = useCallback(async (channels, ops) => {
    const held = new Map(channels.map(c => [c.id, c.world_ids || []]))
    for (const op of ops) _patchChannelInState(op.id, { world_ids: applyOp(held.get(op.id), op) })
    const settled = await Promise.allSettled(ops.map(op => api.moveChannelWorlds(op.id, op)))
    const done = [], failed = []
    settled.forEach((r, i) => {
      const op = ops[i]
      if (r.status === 'fulfilled') {
        _patchChannelInState(op.id, { world_ids: r.value.channel?.world_ids || applyOp(held.get(op.id), op) })
        done.push({ op, before: r.value.before || held.get(op.id) })
      } else {
        _patchChannelInState(op.id, { world_ids: held.get(op.id) })
        failed.push({ id: op.id, error: r.reason?.message || 'refused' })
      }
    })
    return { done, failed }
  }, [_patchChannelInState])

  /* MOVE CHANNELS (select many → Move to…, a drop on a world): out of `from`
     (the world you're in) into `to`; with `keep` (Add to…), into `to` and out
     of nothing; to All channels, out of `from` (Remove from). Resolves to { to, moved, unchanged,
     failed, undo }; undo() puts each moved channel's worlds back, once. */
  const moveChannels = useCallback(async (groupId, ids, { to, from, keep }) => {
    const channels = groupChannelsByGroup[groupId] || []
    const worlds = worldsByGroup[groupId] || []
    const plan = planMove(channels, ids, { to, from, worlds, keep })
    const r = await runWorldOps(channels, plan.ops)
    let undone = null
    const undo = () => undone || (undone = runWorldOps(
      r.done.map(d => ({ id: d.op.id, world_ids: applyOp(d.before, d.op) })),
      r.done.map(d => inverseOp(d.before, d.op)).filter(op => !isNoop(op))))
    return { to: worldName(to, worlds), moved: r.done.map(d => d.op.id), unchanged: plan.unchanged, failed: r.failed, undo }
  }, [groupChannelsByGroup, worldsByGroup, runWorldOps])

  // One channel stands in a world, or leaves it (the row's Worlds checklist)
  const placeChannel = useCallback((channel, op) => runWorldOps([channel], [{ id: channel.id, ...op }]), [runWorldOps])

  // Delete a channel (creator only — removes for everyone)
  const deleteChannel = useCallback(async (channelId) => {
    try {
      await api.deleteChannel(channelId)
      _removeChannelFromState(channelId)
    } catch (err) {
      console.error('Failed to delete channel:', err)
      throw err
    }
  }, [_removeChannelFromState])

  // Token but /me not yet resolved — the post-login loading window. Same inert
  // fallback, plus the cache-restored thread state so a thread that was open
  // last session doesn't flash closed while the app hydrates. threadReplies
  // rides along with threadMessage: a consumer that sees a thread must also see
  // its (possibly cached) replies array, never undefined.
  // NOTE: no early return here — all hooks must be called unconditionally
  // (Rules of Hooks). The loading check is deferred to the final return below.

  const uploadAvatar = useCallback(async (file) => {
    const result = await api.uploadAvatar(file)
    // Optimistically write into the override map; the socket event
    // will redundantly do the same shortly, but we don't want to
    // wait one round-trip to refresh our own hero.
    if (currentUser?.id && result?.avatar_url) {
      setUserAvatars(prev => {
        const next = { ...prev, [currentUser.id]: result.avatar_url }
        cacheSet('userAvatars', next)
        return next
      })
      setCurrentUser(prev => {
        if (!prev) return prev
        const updated = { ...prev, avatar_url: result.avatar_url, avatar_version: result.avatar_version }
        cacheSet('currentUser', updated)
        return updated
      })
    }
    return result
  }, [currentUser])

  const value = {
      authenticated: true,
      currentUser,
      userAvatars,
      uploadAvatar,
      activeChannel,
      selectChannel,
      composingDM,
      startComposeDM,
      cancelComposeDM,
      composingChannel,
      startComposeChannel,
      cancelComposeChannel,
      channels,
      groups,
      epStatus,
      groupChannelsByGroup,
      activeGroup,
      selectGroup,
      refreshGroupChannels,
      activeMessages: messages,
      sendMessage,
      retryUnsent,
      discardUnsent,
      createChannel,
      createGroupChannel,
      directMessages: dmChannels,
      dmChannels,
      fetchDMs,
      createDM,
      createPluginDM,
      members,
      groupMembers,
      activeChannelMembers,
      showThread,
      threadMessage,
      threadReplies,
      openThread,
      openThreadById,
      closeThread,
      recordingsHuddleId,
      openRecordingsSidebar,
      closeRecordingsSidebar,
      sendThreadReply,
      addReaction,
      removeReaction,
      forwardMessage,
      editMessage,
      editingMessage, setEditingMessage,
      bulkEditMode, setBulkEditMode, bulkSelectedMessages, setBulkSelectedMessages,
      replyingToMessage, setReplyingToMessage,
      deleteMessage,
      unreadCounts,
      hasUnreadDMs,
      hasNewActivity,
      newActivityCount,
      markActivitySeen,
      markActivityTimestamp,
      activeHuddle,
      joinedHuddle,
      startHuddle,
      joinHuddle,
      leaveHuddle,
      toggleHuddleMute,
      lastMessageMap,
      addChannelMember,
      removeChannelMember,
      emitTyping,
      typingUsers,
      readReceipts,
      markChannelRead,
      markAsUnread,
      unreadDividerMsgId,
      mutedChannels,
      toggleChannelMute,
      pinnedChannels,
      toggleChannelPin,
      pinnedMessages,
      pinMessage,
      unpinMessage,
      activeHuddles,
      incomingCall,
      dismissIncomingCall,
      joinHuddleFromNotification,
      hasMoreMessages,
      loadingOlder,
      loadOlderMessages,
      leaveChannel,
      renameChannel,
      setChannelProperties,
      worldsByGroup,
      activeWorld,
      enterWorld,
      createWorld,
      renameWorld,
      deleteWorld,
      reorderWorlds,
      moveChannels,
      placeChannel,
      deleteChannel: deleteChannel,
      jumpToMessageId,
      setJumpToMessageId,
      navigateToMessage,
      syncUrl,
  }

  // Parity guard for the pre-auth fallback: every key exposed here MUST have a
  // safe default in CHAT_CONTEXT_FALLBACK, or consumers rendering during the
  // logged-out / loading windows crash on undefined (prod-only, under the
  // splash — the worst kind). Dev-only; free in production builds.
  if (import.meta.env.DEV) {
    for (const k of Object.keys(value)) {
      if (!(k in CHAT_CONTEXT_FALLBACK)) {
        console.warn(`[ChatContext] "${k}" is missing from CHAT_CONTEXT_FALLBACK — add a safe default or pre-auth renders will crash.`)
      }
    }
  }

  // No token — unauthenticated. The complete inert fallback (see its note at
  // the top of the file); never hand-list keys here. Placed AFTER all hooks
  // (with the loading gate below) so the hook count is identical on every
  // render — a gate above any hook crashes with "rendered fewer hooks" the
  // moment a 401 flips token truthy → null mid-session.
  if (!token) {
    return (
      <ChatContext.Provider value={CHAT_CONTEXT_FALLBACK}>
        {children}
      </ChatContext.Provider>
    )
  }

  // Deferred loading gate — placed AFTER all hooks so the hook count is
  // identical on every render regardless of auth state.
  if (loading) {
    return (
      <ChatContext.Provider value={{
        ...CHAT_CONTEXT_FALLBACK,
        authenticated: !!token,
        showThread, threadMessage, threadReplies,
      }}>
        {children}
      </ChatContext.Provider>
    )
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChat() {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChat must be used within ChatProvider')
  return ctx
}
